import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import {
  Area, ComposedChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ReferenceArea, ReferenceLine, ResponsiveContainer,
} from "recharts";
import { apiGetJson, apiPutJson } from "../utils/apiClient";
import LoadingInline from "../components/LoadingInline";
import { useMallContext } from "../contexts/MallContext";
import { useUserContext } from "../contexts/UserContext";
import { isFreePlanUser, isLightPlanUser } from "../utils/plan";
import { openUpgradeForm } from "../components/FreePlanUpgradeDialog";
import "./ItemProfitDetailPage.css";

type ProfitMode = "margin" | "operating";

type DetailHeader = {
  itemName?: string | null;
  genreName?: string | null;
  itemManagementNumber?: string | null;
  itemNumber?: string | null;
  skuManagementNumber?: string | null;
  skuNumber?: string | null;
  imageUrl?: string | null;
  rangeLabel?: string | null;
};

type DetailSummary = {
  grossSalesBeforeCoupon?: number | null;
  salesAfterCoupon?: number | null;
  couponAmount?: number | null;
  couponRate?: number | null;
  units?: number | null;
  orderCount?: number | null;

  marginProfit?: number | null;
  operatingProfit?: number | null;
  marginRate?: number | null;
  operatingRate?: number | null;

  totalCost?: number | null;
  totalCostRate?: number | null;

  unitProfitMargin?: number | null;
  unitProfitOperating?: number | null;
  unitCost?: number | null;

  costSource?: string | null;
  costMissingFlag?: number | null;

  momMarginProfitChangeRate?: number | null;
  momOpProfitChangeRate?: number | null;

  // 送料別の商品：売上に含めた「お客様が払った送料」と、送料別の明細があったか（楽天のみ）
  shippingRevenueAmount?: number | null;
  hasSeparateShipping?: number | null;
};

type CostLine = {
  amount?: number | null;
  rate?: number | null;
  // Amazon のみ: 商品への直課分と、店舗共通費用の売上按分の内訳
  directAmount?: number | null;
  allocatedAmount?: number | null;
};

type CostBreakdown = {
  cogs?: CostLine;
  rakutenFee?: CostLine;
  logistics?: CostLine;
  point?: CostLine;
  affiliate?: CostLine;
  coupon?: CostLine;
  rpp?: CostLine;
  rppexp?: CostLine;
  otherAds?: CostLine;
  fixed?: CostLine;
  total?: CostLine;
};

type ProfitStructureRow = {
  label: string;
  amount?: number | null;
  type?: "base" | "cost" | "profit";
  colorKey?: string | null;
};

type AdDetailRow = {
  adType: string;
  cost?: number | null;
  costRate?: number | null;
  roas?: number | null;
  roas720h?: number | null;
  roas12h?: number | null;
  linkageType?: string | null;
};

type CouponDetailRow = {
  couponCode: string;
  couponName: string;
  orderCount: number;
  totalDiscount: number;
};

type CouponIssuerSummary = {
  couponCount: number;
  totalOrderCount: number;
  totalDiscount: number;
  details: CouponDetailRow[];
};

type CouponDetailRes = {
  store: CouponIssuerSummary;
  rakuten: CouponIssuerSummary;
};

type TrendPoint = {
  date: string;
  sales: number | null;
  units: number | null;
  totalCost: number | null;
  grossProfit: number | null;
};

type TrendPeriod = "month" | "2w" | "30d";

type TrendMetricKey = "sales" | "profitAmount" | "profitRate" | "units";

type TrendDisplayPoint = TrendPoint & {
  label: string;
  profitAmount: number | null;
  profitRate: number | null;
  isWeekend: boolean;
  isSpecialDay: boolean;
};

type RawTrendPoint = {
  date?: string | null;
  reportDate?: string | null;
  report_date?: string | null;
  sales?: number | string | null;
  units?: number | string | null;
  totalCost?: number | string | null;
  total_cost?: number | string | null;
  grossProfit?: number | string | null;
  gross_profit?: number | string | null;
  profit?: number | string | null;
};

type ApiRes = {
  shopUrl: string;
  itemKey: string;
  startDate: string;
  endDate: string;
  profitMode?: ProfitMode;
  header?: DetailHeader;
  summary?: DetailSummary;
  costBreakdown?: CostBreakdown;
  profitStructure?: ProfitStructureRow[];
  insightText?: string | null;
  adDetails?: AdDetailRow[];
  costSource?: string | null;
  costMissingFlag?: number | null;
};

type ListRow = {
  itemKey?: string | null;
  itemManagementNumber?: string | null;
  skuManagementNumber?: string | null;
  skuNumber?: string | null;
  skuCode?: string | null;
  itemCode?: string | null;
  itemName?: string | null;
  genreName?: string | null;
  imageUrl?: string | null;
  units?: number | null;
  orders?: number | null;
  sales?: number | null;
  cost?: number | null;
  feeUnit?: number | null;
  rakutenFeeAmount?: number | null;
  logisticsFeeAmount?: number | null;
  pointFeeAmount?: number | null;
  affiliateFeeAmount?: number | null;
  couponFeeAmount?: number | null;
  rppAmount?: number | null;
  rppexpAmount?: number | null;
  otherAdsAmount?: number | null;
  rakutenFeeUnit?: number | null;
  marketFeeUnit?: number | null;
  logisticsFeeUnit?: number | null;
  pointFeeUnit?: number | null;
  affiliateFeeUnit?: number | null;
  couponFeeUnit?: number | null;
  profitUnit?: number | null;
  profitRate?: number | null;
  fixedCostUnit?: number | null;
  operatingProfitUnit?: number | null;
  operatingProfitRate?: number | null;
  adCostTotal?: number | null;
  rppRoas12h?: number | null;
  rppRoas720h?: number | null;
  caRoas?: number | null;
  costSource?: string | null;
  costMissingFlag?: number | null;
  momProfitChangeRate?: number | null;
  momOpProfitChangeRate?: number | null;
  shippingRevenueAmount?: number | null;
  hasSeparateShipping?: number | null;
};

type ListRes = {
  shopUrl?: string;
  range?: {
    startDate?: string;
    endDate?: string;
  };
  rows?: ListRow[];
};

const nf0 = new Intl.NumberFormat("ja-JP", { maximumFractionDigits: 0 });

const GENRE_COLORS: Record<string, string> = {
  "キッチン用品": "#378ADD",
  "食品": "#1D9E75",
  "靴": "#EF9F27",
  "レディースファッション": "#E24B4A",
  "インナー・下着": "#9DC3E6",
};

const TREND_METRIC_STYLES: Record<TrendMetricKey, { color: string; dashed?: boolean }> = {
  sales: { color: "#378ADD" },
  profitAmount: { color: "#1D9E75", dashed: true },
  profitRate: { color: "#EF9F27", dashed: true },
  units: { color: "#5DCAA5", dashed: true },
};


function num(v: unknown): number {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function nullableNum(v: unknown): number | null {
  if (v === null || v === undefined || v === "") return null;
  const normalized =
    typeof v === "string"
      ? v.replace(/[\s,¥￥]/g, "").replace(/−/g, "-")
      : v;
  const n = Number(normalized);
  return Number.isFinite(n) ? n : null;
}

function sumNumbers(values: Array<number | null | undefined>): number {
  return values.reduce<number>((acc, v) => acc + (v ?? 0), 0);
}

function normalizeTrendRows(rows: RawTrendPoint[]): TrendPoint[] {
  return rows
    .map((r) => {
      const rawDate = r.date ?? r.reportDate ?? r.report_date;
      const date =
        rawDate && typeof rawDate === "object"
          ? String((rawDate as any).value ?? "").trim()
          : String(rawDate ?? "").trim();
      const sales = nullableNum(r.sales);
      const units = nullableNum(r.units);
      const totalCost = nullableNum(r.totalCost ?? r.total_cost);
      const rawGrossProfit = nullableNum(r.grossProfit ?? r.gross_profit ?? r.profit);
      const grossProfit =
        rawGrossProfit !== null
          ? rawGrossProfit
          : sales !== null && totalCost !== null
          ? sales - totalCost
          : null;
      return { date, sales, units, totalCost, grossProfit };
    })
    .filter((r) => r.date !== "");
}

function aggregateItemRowsToDetail(params: {
  itemManagementNumber: string;
  listRes: ListRes;
  profitMode: ProfitMode;
  startDate: string;
  endDate: string;
}) {
  const { itemManagementNumber, listRes, profitMode, startDate, endDate } = params;
  const sourceRows = listRes.rows ?? [];
  const rows = sourceRows.filter((row) => {
    const key = String(row.itemManagementNumber || row.itemCode || "").trim();
    return key === itemManagementNumber;
  });

  const skuKeys = Array.from(
    new Set(rows.map((row) => String(row.itemKey || "").trim()).filter(Boolean)),
  );

  const totalSales = rows.reduce((acc, row) => acc + num(row.sales), 0);
  const totalUnits = rows.reduce((acc, row) => acc + num(row.units), 0);
  const totalOrders = rows.reduce((acc, row) => acc + num(row.orders), 0);
  const totalCogs = rows.reduce((acc, row) => acc + num(row.cost), 0);
  const totalFee = rows.reduce((acc, row) => acc + num(row.feeUnit), 0);
  const totalAdCost = rows.reduce((acc, row) => acc + num(row.adCostTotal), 0);
  const totalRakutenFeeAmount = rows.reduce((acc, row) => acc + num(row.rakutenFeeAmount), 0);
  // Amazon の一覧APIは物流費を logisticsFeeUnit という名前で返すため、そちらもフォールバック先にする
  const totalLogisticsFeeAmount = rows.reduce(
    (acc, row) => acc + (nullableNum(row.logisticsFeeAmount) ?? num(row.logisticsFeeUnit)),
    0,
  );
  // Amazon のみ: 「この商品に直接かかった実額」と「店舗共通費用の売上按分」の内訳
  const totalFeeDirectAmount = rows.reduce((acc, row) => acc + num((row as any).feeDirectAmount), 0);
  const totalFeeAllocatedAmount = rows.reduce((acc, row) => acc + num((row as any).feeAllocatedAmount), 0);
  const totalLogisticsDirectAmount = rows.reduce((acc, row) => acc + num((row as any).logisticsDirectAmount), 0);
  const totalLogisticsAllocatedAmount = rows.reduce((acc, row) => acc + num((row as any).logisticsAllocatedAmount), 0);
  const totalPointFeeAmount = rows.reduce((acc, row) => acc + num(row.pointFeeAmount), 0);
  const totalAffiliateFeeAmount = rows.reduce((acc, row) => acc + num(row.affiliateFeeAmount), 0);
  const totalCouponFeeAmount = rows.reduce((acc, row) => acc + num(row.couponFeeAmount), 0);
  const totalRppAmount = rows.reduce((acc, row) => acc + num(row.rppAmount), 0);
  const totalRppexpAmount = rows.reduce((acc, row) => acc + num(row.rppexpAmount), 0);
  const totalOtherAdsAmount = rows.reduce((acc, row) => acc + num(row.otherAdsAmount), 0);
  const hasDetailedFeeBreakdown = rows.some((row) =>
    nullableNum(row.rakutenFeeAmount) !== null ||
    nullableNum(row.logisticsFeeAmount) !== null ||
    nullableNum(row.pointFeeAmount) !== null ||
    nullableNum(row.affiliateFeeAmount) !== null ||
    nullableNum(row.couponFeeAmount) !== null ||
    nullableNum(row.rppAmount) !== null ||
    nullableNum(row.rppexpAmount) !== null ||
    nullableNum(row.otherAdsAmount) !== null,
  );
  const totalFixed = rows.reduce((acc, row) => acc + num(row.fixedCostUnit), 0);
  const totalMarginProfit = rows.reduce((acc, row) => acc + num(row.profitUnit), 0);
  const totalOperatingProfit = rows.reduce((acc, row) => {
    if (row.operatingProfitUnit !== null && row.operatingProfitUnit !== undefined) {
      return acc + num(row.operatingProfitUnit);
    }
    return acc + num(row.profitUnit) - num(row.fixedCostUnit);
  }, 0);

  const marginRate = totalSales > 0 ? totalMarginProfit / totalSales : null;
  const operatingRate = totalSales > 0 ? totalOperatingProfit / totalSales : null;
  const totalCostMargin = totalCogs + totalFee;
  const totalCostOperating = totalCostMargin + totalFixed;
  const totalCost = profitMode === "margin" ? totalCostMargin : totalCostOperating;

  const uniqueNames = Array.from(
    new Set(rows.map((row) => String(row.itemName || "").trim()).filter(Boolean)),
  );
  const uniqueGenres = Array.from(
    new Set(rows.map((row) => String(row.genreName || "").trim()).filter(Boolean)),
  );
  const firstImage = rows
    .map((row) => String(row.imageUrl || "").trim())
    .find((url) => url.length > 0) || null;

  const primaryName = uniqueNames[0] || itemManagementNumber;
  const itemName = uniqueNames.length > 1 ? `${primaryName} ほか${uniqueNames.length - 1}SKU` : primaryName;

  const avgRate = (
    getter: (row: ListRow) => number | null,
    weightGetter: (row: ListRow) => number,
  ) => {
    let totalWeight = 0;
    let weighted = 0;
    for (const row of rows) {
      const value = getter(row);
      if (value === null) continue;
      const weight = Math.max(0, weightGetter(row));
      if (weight <= 0) continue;
      totalWeight += weight;
      weighted += value * weight;
    }
    if (totalWeight <= 0) return null;
    return weighted / totalWeight;
  };

  const momMarginRate = avgRate(
    (row) => nullableNum(row.momProfitChangeRate),
    (row) => Math.abs(num(row.profitUnit)),
  );
  const momOpRate = avgRate(
    (row) => nullableNum(row.momOpProfitChangeRate),
    (row) => Math.abs(num(row.operatingProfitUnit)),
  );

  const rppRoas12h = avgRate((row) => nullableNum(row.rppRoas12h), (row) => num(row.sales));
  const rppRoas720h = avgRate((row) => nullableNum(row.rppRoas720h), (row) => num(row.sales));
  const caRoas = avgRate((row) => nullableNum(row.caRoas), (row) => num(row.sales));

  const displayProfit = profitMode === "margin" ? totalMarginProfit : totalOperatingProfit;

  const detail: ApiRes = {
    shopUrl: String(listRes.shopUrl || ""),
    itemKey: skuKeys[0] || itemManagementNumber,
    startDate,
    endDate,
    profitMode,
    header: {
      itemName,
      genreName: uniqueGenres[0] || null,
      itemManagementNumber,
      imageUrl: firstImage,
      rangeLabel: `${startDate} 〜 ${endDate}`,
    },
    summary: {
      grossSalesBeforeCoupon: null,
      salesAfterCoupon: totalSales,
      couponAmount: null,
      couponRate: null,
      units: totalUnits,
      orderCount: totalOrders,
      marginProfit: totalMarginProfit,
      operatingProfit: totalOperatingProfit,
      marginRate,
      operatingRate,
      totalCost,
      totalCostRate: totalSales > 0 ? totalCost / totalSales : null,
      unitProfitMargin: totalUnits > 0 ? totalMarginProfit / totalUnits : null,
      unitProfitOperating: totalUnits > 0 ? totalOperatingProfit / totalUnits : null,
      unitCost: totalUnits > 0 ? totalCogs / totalUnits : null,
      costSource: rows.some((row) => num(row.costMissingFlag) > 0)
        ? "mixed"
        : Array.from(new Set(rows.map((row) => String(row.costSource || "").trim()).filter(Boolean))).join(",") || null,
      costMissingFlag: rows.some((row) => num(row.costMissingFlag) > 0) ? 1 : 0,
      momMarginProfitChangeRate: momMarginRate,
      momOpProfitChangeRate: momOpRate,
      shippingRevenueAmount: rows.reduce((acc, row) => acc + num(row.shippingRevenueAmount), 0),
      hasSeparateShipping: rows.some((row) => num(row.hasSeparateShipping) > 0) ? 1 : 0,
    },
    costBreakdown: {
      cogs: {
        amount: totalCogs,
        rate: totalSales > 0 ? totalCogs / totalSales : null,
      },
      rakutenFee: {
        amount: hasDetailedFeeBreakdown ? totalRakutenFeeAmount : totalFee,
        rate: totalSales > 0 ? (hasDetailedFeeBreakdown ? totalRakutenFeeAmount : totalFee) / totalSales : null,
        directAmount: totalFeeDirectAmount,
        allocatedAmount: totalFeeAllocatedAmount,
      },
      logistics: {
        amount: totalLogisticsFeeAmount,
        rate: totalSales > 0 ? totalLogisticsFeeAmount / totalSales : null,
        directAmount: totalLogisticsDirectAmount,
        allocatedAmount: totalLogisticsAllocatedAmount,
      },
      point: {
        amount: hasDetailedFeeBreakdown ? totalPointFeeAmount : null,
        rate: totalSales > 0 && hasDetailedFeeBreakdown ? totalPointFeeAmount / totalSales : null,
      },
      affiliate: {
        amount: hasDetailedFeeBreakdown ? totalAffiliateFeeAmount : null,
        rate: totalSales > 0 && hasDetailedFeeBreakdown ? totalAffiliateFeeAmount / totalSales : null,
      },
      coupon: {
        amount: hasDetailedFeeBreakdown ? totalCouponFeeAmount : null,
        rate: totalSales > 0 && hasDetailedFeeBreakdown ? totalCouponFeeAmount / totalSales : null,
      },
      rpp: {
        amount: hasDetailedFeeBreakdown ? totalRppAmount : totalAdCost,
        rate: totalSales > 0 ? (hasDetailedFeeBreakdown ? totalRppAmount : totalAdCost) / totalSales : null,
      },
      rppexp: {
        amount: hasDetailedFeeBreakdown ? totalRppexpAmount : 0,
        rate: totalSales > 0 ? (hasDetailedFeeBreakdown ? totalRppexpAmount : 0) / totalSales : null,
      },
      otherAds: {
        amount: hasDetailedFeeBreakdown ? totalOtherAdsAmount : 0,
        rate: totalSales > 0 ? (hasDetailedFeeBreakdown ? totalOtherAdsAmount : 0) / totalSales : null,
      },
      fixed: {
        amount: totalFixed,
        rate: totalSales > 0 ? totalFixed / totalSales : null,
      },
      total: {
        amount: totalCost,
        rate: totalSales > 0 ? totalCost / totalSales : null,
      },
    },
    profitStructure:
      profitMode === "operating"
        ? [
            { label: "売上", amount: totalSales, type: "base" },
            { label: "原価", amount: -totalCogs, type: "cost" },
            { label: "費用合計", amount: -totalFee, type: "cost" },
            { label: "固定費", amount: -totalFixed, type: "cost" },
            {
              label: "営業利益",
              amount: displayProfit,
              type: "profit",
            },
          ]
        : [
            { label: "売上", amount: totalSales, type: "base" },
            { label: "原価", amount: -totalCogs, type: "cost" },
            { label: "費用合計", amount: -totalFee, type: "cost" },
            {
              label: "限界利益",
              amount: displayProfit,
              type: "profit",
            },
          ],
    adDetails: [
      ...(hasDetailedFeeBreakdown
        ? [
            {
              adType: "RPP",
              cost: totalRppAmount,
              costRate: totalSales > 0 ? totalRppAmount / totalSales : null,
              roas: rppRoas12h,
              roas720h: rppRoas720h,
              roas12h: rppRoas12h,
              linkageType: "直接",
            },
            {
              adType: "RPP-EXP",
              cost: totalRppexpAmount,
              costRate: totalSales > 0 ? totalRppexpAmount / totalSales : null,
              linkageType: "直接",
            },
            {
              adType: "CA広告",
              cost: null,
              costRate: null,
              roas: caRoas,
              linkageType: "費用はその他広告に含む",
            },
            {
              adType: "その他広告",
              cost: totalOtherAdsAmount,
              costRate: totalSales > 0 ? totalOtherAdsAmount / totalSales : null,
              linkageType: "按分",
            },
          ]
        : []),
      {
        adType: "広告費合計",
        cost: totalAdCost,
        costRate: totalSales > 0 ? totalAdCost / totalSales : null,
      },
    ],
    insightText:
      "商品単位表示では、商品管理番号配下のSKUを集約して表示しています。広告費・原価・費用構成の偏りを確認してください。",
    costSource: rows.some((row) => num(row.costMissingFlag) > 0) ? "mixed" : null,
    costMissingFlag: rows.some((row) => num(row.costMissingFlag) > 0) ? 1 : 0,
  };

  return { detail, rows, skuKeys };
}

function buildSkuDetailFromListRow(params: {
  itemKey: string;
  listRes: ListRes;
  profitMode: ProfitMode;
  startDate: string;
  endDate: string;
}) {
  const { itemKey, listRes, profitMode, startDate, endDate } = params;
  const row = (listRes.rows ?? []).find(
    (r) => String(r.itemKey || "").trim() === String(itemKey || "").trim(),
  );

  if (!row) return null;

  const sales = num(row.sales);
  const units = num(row.units);
  const orders = num(row.orders);

  const cogs = nullableNum(row.cost);
  const feeUnit = nullableNum(row.feeUnit);
  const fixedCost = nullableNum(row.fixedCostUnit);
  const adCost = nullableNum(row.adCostTotal);

  let marginProfit = nullableNum(row.profitUnit);
  if (marginProfit === null && cogs !== null && feeUnit !== null) {
    marginProfit = sales - cogs - feeUnit;
  }

  let operatingProfit = nullableNum(row.operatingProfitUnit);
  if (operatingProfit === null && marginProfit !== null && fixedCost !== null) {
    operatingProfit = marginProfit - fixedCost;
  }

  const marginRate = nullableNum(row.profitRate) ?? (sales > 0 && marginProfit !== null ? marginProfit / sales : null);
  const operatingRate =
    nullableNum(row.operatingProfitRate) ??
    (sales > 0 && operatingProfit !== null ? operatingProfit / sales : null);

  const totalCostMargin = cogs !== null && feeUnit !== null ? cogs + feeUnit : null;
  const totalCostOperating =
    totalCostMargin !== null && fixedCost !== null ? totalCostMargin + fixedCost : null;
  const totalCost = profitMode === "margin" ? totalCostMargin : totalCostOperating;

  const rakutenFeeAmount = nullableNum(row.rakutenFeeAmount);
  const logisticsFeeAmount = nullableNum(row.logisticsFeeAmount);
  const logisticsAmount = logisticsFeeAmount ?? nullableNum(row.logisticsFeeUnit);
  // Amazon のみ: 直課/按分の内訳（楽天は undefined のまま）
  const feeDirectAmount = nullableNum((row as any).feeDirectAmount);
  const feeAllocatedAmount = nullableNum((row as any).feeAllocatedAmount);
  const logisticsDirectAmount = nullableNum((row as any).logisticsDirectAmount);
  const logisticsAllocatedAmount = nullableNum((row as any).logisticsAllocatedAmount);
  const pointFeeAmount = nullableNum(row.pointFeeAmount);
  const affiliateFeeAmount = nullableNum(row.affiliateFeeAmount);
  const couponFeeAmount = nullableNum(row.couponFeeAmount);
  const rppAmount = nullableNum(row.rppAmount);
  const rppexpAmount = nullableNum(row.rppexpAmount);
  const otherAdsAmount = nullableNum(row.otherAdsAmount);
  const rppRoas12h = nullableNum(row.rppRoas12h);
  const rppRoas720h = nullableNum(row.rppRoas720h);
  const caRoas = nullableNum(row.caRoas);

  const hasDetailedFeeBreakdown = [
    rakutenFeeAmount,
    logisticsFeeAmount,
    pointFeeAmount,
    affiliateFeeAmount,
    couponFeeAmount,
    rppAmount,
    rppexpAmount,
    otherAdsAmount,
  ].some((v) => v !== null);

  const detail: ApiRes = {
    shopUrl: String(listRes.shopUrl || ""),
    itemKey: String(row.itemKey || itemKey),
    startDate,
    endDate,
    profitMode,
    header: {
      itemName: String(row.itemName || ""),
      genreName: String(row.genreName || "") || null,
      itemManagementNumber: String(row.itemManagementNumber || row.itemCode || "") || null,
      itemNumber: String(row.itemManagementNumber || row.itemCode || "") || null,
      skuManagementNumber: String(row.skuManagementNumber || "") || null,
      skuNumber: String(row.skuManagementNumber || "") || null,
      imageUrl: String(row.imageUrl || "") || null,
      rangeLabel: `${startDate} 〜 ${endDate}`,
    },
    summary: {
      grossSalesBeforeCoupon: null,
      salesAfterCoupon: sales,
      couponAmount: null,
      couponRate: null,
      units,
      orderCount: orders,
      marginProfit,
      operatingProfit,
      marginRate,
      operatingRate,
      totalCost,
      totalCostRate: sales > 0 && totalCost !== null ? totalCost / sales : null,
      unitProfitMargin: units > 0 && marginProfit !== null ? marginProfit / units : null,
      unitProfitOperating: units > 0 && operatingProfit !== null ? operatingProfit / units : null,
      unitCost: units > 0 && cogs !== null ? cogs / units : null,
      costSource: String(row.costSource || "") || null,
      costMissingFlag: nullableNum(row.costMissingFlag) ?? 0,
      momMarginProfitChangeRate: nullableNum(row.momProfitChangeRate),
      momOpProfitChangeRate: nullableNum(row.momOpProfitChangeRate),
      shippingRevenueAmount: nullableNum(row.shippingRevenueAmount),
      hasSeparateShipping: nullableNum(row.hasSeparateShipping) ?? 0,
    },
    costBreakdown: {
      cogs: {
        amount: cogs,
        rate: sales > 0 && cogs !== null ? cogs / sales : null,
      },
      rakutenFee: {
        amount: hasDetailedFeeBreakdown ? rakutenFeeAmount : feeUnit,
        rate:
          sales > 0
            ? hasDetailedFeeBreakdown
              ? rakutenFeeAmount !== null
                ? rakutenFeeAmount / sales
                : null
              : feeUnit !== null
                ? feeUnit / sales
                : null
            : null,
        directAmount: feeDirectAmount,
        allocatedAmount: feeAllocatedAmount,
      },
      logistics: {
        // Amazon の一覧APIは物流費を logisticsFeeUnit という名前で返すため、そちらもフォールバック先にする。
        // 見落とすと変動費から物流費が丸ごと抜け、一覧の利益額と一致しなくなる
        amount: logisticsAmount,
        rate: sales > 0 && logisticsAmount !== null ? logisticsAmount / sales : null,
        directAmount: logisticsDirectAmount,
        allocatedAmount: logisticsAllocatedAmount,
      },
      point: {
        amount: hasDetailedFeeBreakdown ? pointFeeAmount : null,
        rate: sales > 0 && hasDetailedFeeBreakdown && pointFeeAmount !== null ? pointFeeAmount / sales : null,
      },
      affiliate: {
        amount: hasDetailedFeeBreakdown ? affiliateFeeAmount : null,
        rate: sales > 0 && hasDetailedFeeBreakdown && affiliateFeeAmount !== null ? affiliateFeeAmount / sales : null,
      },
      coupon: {
        amount: hasDetailedFeeBreakdown ? couponFeeAmount : null,
        rate: sales > 0 && hasDetailedFeeBreakdown && couponFeeAmount !== null ? couponFeeAmount / sales : null,
      },
      rpp: {
        amount: hasDetailedFeeBreakdown ? rppAmount : adCost,
        rate:
          sales > 0
            ? hasDetailedFeeBreakdown
              ? rppAmount !== null
                ? rppAmount / sales
                : null
              : adCost !== null
                ? adCost / sales
                : null
            : null,
      },
      rppexp: {
        amount: hasDetailedFeeBreakdown ? rppexpAmount : 0,
        rate: sales > 0 && hasDetailedFeeBreakdown && rppexpAmount !== null ? rppexpAmount / sales : null,
      },
      otherAds: {
        amount: hasDetailedFeeBreakdown ? otherAdsAmount : 0,
        rate: sales > 0 && hasDetailedFeeBreakdown && otherAdsAmount !== null ? otherAdsAmount / sales : null,
      },
      fixed: {
        amount: fixedCost,
        rate: sales > 0 && fixedCost !== null ? fixedCost / sales : null,
      },
      total: {
        amount: totalCost,
        rate: sales > 0 && totalCost !== null ? totalCost / sales : null,
      },
    },
    profitStructure:
      profitMode === "operating"
        ? [
            { label: "売上", amount: sales, type: "base" },
            { label: "原価", amount: cogs !== null ? -cogs : null, type: "cost" },
            { label: "費用合計", amount: feeUnit !== null ? -feeUnit : null, type: "cost" },
            { label: "固定費", amount: fixedCost !== null ? -fixedCost : null, type: "cost" },
            { label: "営業利益", amount: operatingProfit, type: "profit" },
          ]
        : [
            { label: "売上", amount: sales, type: "base" },
            { label: "原価", amount: cogs !== null ? -cogs : null, type: "cost" },
            { label: "費用合計", amount: feeUnit !== null ? -feeUnit : null, type: "cost" },
            { label: "限界利益", amount: marginProfit, type: "profit" },
          ],
    adDetails: [
      ...(hasDetailedFeeBreakdown
        ? [
            {
              adType: "RPP",
              cost: rppAmount,
              costRate: sales > 0 && rppAmount !== null ? rppAmount / sales : null,
              roas: rppRoas12h,
              roas720h: rppRoas720h,
              roas12h: rppRoas12h,
              linkageType: "直接",
            },
            {
              adType: "RPP-EXP",
              cost: rppexpAmount,
              costRate: sales > 0 && rppexpAmount !== null ? rppexpAmount / sales : null,
              linkageType: "直接",
            },
            {
              adType: "CA広告",
              cost: null,
              costRate: null,
              roas: caRoas,
              linkageType: "費用はその他広告に含む",
            },
            {
              adType: "その他広告",
              cost: otherAdsAmount,
              costRate: sales > 0 && otherAdsAmount !== null ? otherAdsAmount / sales : null,
              linkageType: "按分",
            },
          ]
        : []),
      {
        adType: "広告費合計",
        cost: adCost,
        costRate: sales > 0 && adCost !== null ? adCost / sales : null,
      },
    ],
    insightText:
      "一覧と同じSKU行を基準に詳細を表示しています。費用内訳と利益率を確認し、改善余地をチェックしてください。",
    costSource: String(row.costSource || "") || null,
    costMissingFlag: nullableNum(row.costMissingFlag) ?? 0,
  };

  return detail;
}

function fmtYen(v: unknown) {
  const n = nullableNum(v);
  if (n === null) return "—";
  if (n < 0) return `−¥${nf0.format(Math.abs(Math.round(n)))}`;
  return `¥${nf0.format(Math.round(n))}`;
}

function fmtPct(v: unknown, digits = 1) {
  const n = nullableNum(v);
  if (n === null) return "—";
  return `${(n * 100).toFixed(digits)}%`;
}

function fmtPctMaybeWhole(v: unknown) {
  const n = nullableNum(v);
  if (n === null) return "—";
  return `${Math.round(n)}%`;
}

function fmtTrendAxis(metric: TrendMetricKey, value: number | null | undefined) {
  if (value === null || value === undefined || !Number.isFinite(value)) return "—";
  if (metric === "profitRate") return `${value.toFixed(0)}%`;
  if (metric === "units") return `${Math.round(value)}`;
  const abs = Math.abs(value);
  if (abs >= 10000) return `${(value / 10000).toFixed(abs >= 100000 ? 0 : 1)}万`;
  return String(Math.round(value));
}

function fmtTrendValue(metric: TrendMetricKey, value: number | null | undefined) {
  if (value === null || value === undefined || !Number.isFinite(value)) return "—";
  if (metric === "profitRate") return `${value.toFixed(1)}%`;
  if (metric === "units") return `${nf0.format(Math.round(value))}個`;
  return `¥${nf0.format(Math.round(value))}`;
}

function getStructureBarColor(
  label: string,
  type?: "base" | "cost" | "profit",
  colorKey?: string | null,
) {
  const normalized = String(label || "").toLowerCase();

  if (normalized.includes("原価")) return "#E24B4A";
  if (normalized.includes("楽天手数料") || normalized.includes("販売手数料")) return "#EF9F27";
  if (normalized.includes("物流費")) return "#85B7EB";
  if (normalized.includes("ポイント")) return "#1D9E75";
  if (normalized.includes("アフィリエイト")) return "#97C459";
  if (normalized.includes("クーポン")) return "#C0DD97";
  if (normalized.includes("rpp-exp")) return "#5B9BD5";
  if (normalized.includes("rpp")) return "#378ADD";
  if (normalized.includes("tda") || normalized.includes("その他")) return "#9DC3E6";
  if (normalized.includes("広告")) return "#378ADD";
  if (normalized.includes("固定費")) return "#B4B2A9";
  if (normalized.includes("営業利益")) return "#185FA5";
  if (normalized.includes("限界利益")) return "#1D9E75";
  if (normalized.includes("売上")) return "#1D9E75";

  if (type === "profit") return "#1D9E75";
  if (type === "cost") return "#E24B4A";
  if (colorKey === "green") return "#1D9E75";
  if (colorKey === "red") return "#E24B4A";
  return "#9DAAB8";
}

const ItemProfitUpgradeDialog = ({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) => {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/45 px-4">
      <div className="w-full max-w-[480px] overflow-hidden rounded-xl bg-white shadow-[0_8px_32px_rgba(0,0,0,0.18)]">
        <div className="bg-[#4a72c4] px-7 pb-5 pt-6">
          <div className="mb-2 text-[11px] font-medium tracking-[0.08em] text-[#cfdcf5]">
            スタンダードプラン以上の機能
          </div>
          <div className="text-[17px] font-semibold leading-[1.5] text-white">
            商品別利益はスタンダードプラン以上でご利用いただけます
          </div>
        </div>

        <div className="px-7 py-6">
          <div className="mb-[18px] grid grid-cols-1 gap-[10px] sm:grid-cols-2">
            <div className="rounded-lg bg-[#f0f4fb] px-4 py-[14px]">
              <div className="mb-1 text-[12px] font-semibold text-[#3a5aa0]">
                商品ごとの利益を比較
              </div>
              <div className="text-[12px] leading-[1.5] text-[#555]">
                商品管理番号ごとに、本当の利益を自動で集計
              </div>
            </div>

            <div className="rounded-lg bg-[#f0f4fb] px-4 py-[14px]">
              <div className="mb-1 text-[12px] font-semibold text-[#3a5aa0]">
                商品ごとの各種費用を特定
              </div>
              <div className="text-[12px] leading-[1.5] text-[#555]">
                商品ごとにROAS、ポイント、クーポンコストも自動で集計
              </div>
            </div>
          </div>

          <p className="mb-5 text-[13px] leading-[1.7] text-[#555]">
            「売上と利益はどう関係している?」売上ではなく
            <strong className="font-semibold text-[#333]">限界利益額</strong>
            で判断し、儲かるショップ運営を。
          </p>

          <div className="flex gap-[10px] border-t border-[#eee] pt-4">
            <button
              className="flex-1 rounded-lg bg-[#4a72c4] px-4 py-[11px] text-[14px] font-semibold text-white transition hover:bg-[#3a5fad]"
              onClick={() => openUpgradeForm("商品別利益分析")}
            >
              プランをアップグレードする →
            </button>

            <button
              className="whitespace-nowrap rounded-lg border border-[#ddd] bg-white px-4 py-[11px] text-[13px] text-[#888] transition hover:bg-[#f5f5f5]"
              onClick={onClose}
            >
              閉じる
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default function ItemProfitDetailPage() {
  const user = useUserContext();
  // Amazon では楽天手数料の欄に販売手数料が入るため表示名を切り替える
  const { mall } = useMallContext();
  const feeLabel = mall === "amazon" ? "販売手数料" : mall === "yahoo" ? "手数料・システム利用料" : "楽天手数料";
  const navigate = useNavigate();
  const location = useLocation();

  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);

  const itemKey = searchParams.get("itemKey") || "";
  const aggregateUnitRaw = (searchParams.get("aggregateUnit") || "sku") as "sku" | "item";
  const itemManagementNumberParam = String(searchParams.get("itemManagementNumber") || "").trim();
  const isItemAggregateMode = aggregateUnitRaw === "item" && itemManagementNumberParam.length > 0;
  const mode = (searchParams.get("mode") || "month") as "month" | "custom";
  const backProfitMode = (searchParams.get("profitMode") || "margin") as ProfitMode;
  const qsStartDate =
    searchParams.get("startDate") || dayjs().startOf("month").format("YYYY-MM-DD");
  const qsEndDate = searchParams.get("endDate") || dayjs().format("YYYY-MM-DD");

  const [profitMode, setProfitMode] = useState<ProfitMode>(backProfitMode);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<ApiRes | null>(null);
  const [autoImageFetchKeys, setAutoImageFetchKeys] = useState<Set<string>>(new Set());
  const [imageRefreshLoading, setImageRefreshLoading] = useState(false);
  const [imageCacheBuster, setImageCacheBuster] = useState<number>(Date.now());
  const [simPrice, setSimPrice] = useState("");
  const [simUnits, setSimUnits] = useState("");
  const [simCost, setSimCost] = useState("");

  const [couponData, setCouponData] = useState<CouponDetailRes | null>(null);
  const [couponReferenceData, setCouponReferenceData] = useState<CouponDetailRes | null>(null);
  const [couponStoreOpen, setCouponStoreOpen] = useState(false);
  const [couponRakutenOpen, setCouponRakutenOpen] = useState(false);
  const [trendData, setTrendData] = useState<TrendPoint[]>([]);
  const [trendFetchFailed, setTrendFetchFailed] = useState(false);
  const [trendFetchMessage, setTrendFetchMessage] = useState<string | null>(null);
  const [itemAggregateSkuKeys, setItemAggregateSkuKeys] = useState<string[]>([]);
  const [trendPeriod, setTrendPeriod] = useState<TrendPeriod>("2w");
  const [trendPrimaryMetric, setTrendPrimaryMetric] = useState<TrendMetricKey>("sales");
  const [trendSecondaryMetric, setTrendSecondaryMetric] = useState<TrendMetricKey | null>("profitAmount");
  const [activeTrendPreset, setActiveTrendPreset] = useState<number | null>(0);
  const [bepDetailOpen, setBepDetailOpen] = useState(false);
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);

  const isRestrictedPlan = useMemo(
    () => isFreePlanUser(user) || isLightPlanUser(user),
    [user],
  );

  const fetchItemAggregateData = useCallback(async () => {
    const targetItemCode = String(itemManagementNumberParam || "").trim();
    if (!targetItemCode) {
      throw new Error("itemManagementNumber が指定されていません。");
    }

    const p = new URLSearchParams();
    p.set("mode", mode);
    p.set("profitMode", profitMode);
    p.set("startDate", qsStartDate);
    p.set("endDate", qsEndDate);

    const listRes = (await apiGetJson(
      `/api/profit/item-profit-list?${p.toString()}`,
    )) as ListRes;

    const startDate = String(listRes.range?.startDate || qsStartDate);
    const endDate = String(listRes.range?.endDate || qsEndDate);
    const aggregated = aggregateItemRowsToDetail({
      itemManagementNumber: targetItemCode,
      listRes,
      profitMode,
      startDate,
      endDate,
    });

    if (aggregated.rows.length === 0) {
      throw new Error(`商品管理番号 ${targetItemCode} のデータが見つかりません。`);
    }

    return aggregated;
  }, [itemManagementNumberParam, mode, profitMode, qsEndDate, qsStartDate]);

  const fetchSkuDetailFromListData = useCallback(async () => {
    if (!itemKey) {
      throw new Error("itemKey が指定されていません。");
    }

    const p = new URLSearchParams();
    p.set("mode", mode);
    p.set("profitMode", profitMode);
    p.set("startDate", qsStartDate);
    p.set("endDate", qsEndDate);

    const listRes = (await apiGetJson(
      `/api/profit/item-profit-list?${p.toString()}`,
    )) as ListRes;

    const startDate = String(listRes.range?.startDate || qsStartDate);
    const endDate = String(listRes.range?.endDate || qsEndDate);
    const detail = buildSkuDetailFromListRow({
      itemKey,
      listRes,
      profitMode,
      startDate,
      endDate,
    });

    if (!detail) {
      throw new Error(`itemKey ${itemKey} のSKUデータが一覧に見つかりません。`);
    }

    return detail;
  }, [itemKey, mode, profitMode, qsEndDate, qsStartDate]);

  const fetchData = useCallback(async () => {
    if (isRestrictedPlan) {
      setLoading(false);
      setData(null);
      return;
    }

    if (!isItemAggregateMode && !itemKey) {
      setError("itemKey が指定されていません。");
      return;
    }

    if (isItemAggregateMode && !String(itemManagementNumberParam || "").trim()) {
      setError("itemManagementNumber が指定されていません。");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      if (isItemAggregateMode) {
        const aggregated = await fetchItemAggregateData();
        setData(aggregated.detail);
        setItemAggregateSkuKeys(aggregated.skuKeys);
      } else {
        const detail = await fetchSkuDetailFromListData();
        setData(detail);
        setItemAggregateSkuKeys([]);
      }
    } catch (e: any) {
      setError(e?.message || "商品別利益詳細を取得できませんでした。");
    } finally {
      setLoading(false);
    }
  }, [
    fetchItemAggregateData,
    fetchSkuDetailFromListData,
    isItemAggregateMode,
    isRestrictedPlan,
    itemKey,
    itemManagementNumberParam,
    profitMode,
    qsEndDate,
    qsStartDate,
  ]);

  useEffect(() => {
    if (isRestrictedPlan) {
      setShowUpgradeDialog(true);
      return;
    }
    fetchData();
  }, [fetchData, isRestrictedPlan]);

  const fetchCouponData = useCallback(async () => {
    if (isRestrictedPlan) return;
    if (!itemKey && !isItemAggregateMode) return;
    try {
      const itemCodeForCoupon = String(
        itemManagementNumberParam ||
          itemKey.split("-")[0] ||
          "",
      ).trim();
      const skuCodeForCoupon = isItemAggregateMode
        ? ""
        : String(
            searchParams.get("skuManagementNumber") ||
              searchParams.get("skuCode") ||
              "",
          ).trim();

      const p = new URLSearchParams();
      p.set("startDate", qsStartDate);
      p.set("endDate", qsEndDate);
      if (itemCodeForCoupon) {
        p.set("itemManagementNumber", itemCodeForCoupon);
      }
      if (skuCodeForCoupon) {
        p.set("skuCode", skuCodeForCoupon);
      }
      const res = (await apiGetJson(
        `/api/profit/coupon-detail?${p.toString()}`,
      )) as CouponDetailRes;
      setCouponData(res);
    } catch {
      // クーポンデータ取得失敗は非致命的
    }
  }, [
    itemManagementNumberParam,
    itemKey,
    searchParams,
    qsStartDate,
    qsEndDate,
    isRestrictedPlan,
    isItemAggregateMode,
  ]);

  useEffect(() => {
    fetchCouponData();
  }, [fetchCouponData]);

  const header = data?.header ?? {};
  const couponReferenceItemCode = useMemo(() => {
    return String(
      itemManagementNumberParam ||
        header.itemManagementNumber ||
        header.itemNumber ||
        itemKey.split("-")[0] ||
        "",
    ).trim();
  }, [header.itemManagementNumber, header.itemNumber, itemKey, itemManagementNumberParam]);

  const couponReferenceSkuCode = useMemo(() => {
    if (isItemAggregateMode) return "";
    return String(
      header.skuManagementNumber ||
        header.skuNumber ||
        searchParams.get("skuManagementNumber") ||
        searchParams.get("skuCode") ||
        "",
    ).trim();
  }, [
    header.skuManagementNumber,
    header.skuNumber,
    isItemAggregateMode,
    searchParams,
  ]);

  const fetchCouponReferenceData = useCallback(async () => {
    if (isRestrictedPlan) return;
    if (!couponReferenceItemCode) return;
    try {
      const p = new URLSearchParams();
      p.set("startDate", qsStartDate);
      p.set("endDate", qsEndDate);
      p.set("itemManagementNumber", couponReferenceItemCode);
      if (couponReferenceSkuCode) {
        p.set("skuCode", couponReferenceSkuCode);
      }
      const res = (await apiGetJson(
        `/api/profit/coupon-detail?${p.toString()}`,
      )) as CouponDetailRes;
      setCouponReferenceData(res);
    } catch {
      // 参考値取得失敗は非致命的
    }
  }, [
    couponReferenceItemCode,
    couponReferenceSkuCode,
    qsStartDate,
    qsEndDate,
    isRestrictedPlan,
  ]);

  useEffect(() => {
    fetchCouponReferenceData();
  }, [fetchCouponReferenceData]);

  const fetchTrendData = useCallback(async () => {
    if (isRestrictedPlan) return;
    if (!isItemAggregateMode && !itemKey) return;
    try {
      setTrendFetchFailed(false);
      setTrendFetchMessage(null);

      if (isItemAggregateMode) {
        const skuKeys = itemAggregateSkuKeys.length > 0
          ? itemAggregateSkuKeys
          : (await fetchItemAggregateData()).skuKeys;

        if (skuKeys.length === 0) {
          setTrendData([]);
          return;
        }

        const trendResults = await Promise.all(
          skuKeys.map(async (skuKey) => {
            const p = new URLSearchParams();
            p.set("itemKey", skuKey);
            p.set("startDate", qsStartDate);
            p.set("endDate", qsEndDate);
            const res = (await apiGetJson(
              `/api/profit/profit-trend?${p.toString()}`,
            )) as { data?: RawTrendPoint[] };
            return normalizeTrendRows(res.data ?? []);
          }),
        );

        const byDate = new Map<string, TrendPoint>();
        for (const rows of trendResults) {
          for (const row of rows) {
            const prev = byDate.get(row.date);
            if (!prev) {
              byDate.set(row.date, { ...row });
              continue;
            }
            byDate.set(row.date, {
              date: row.date,
              sales: (prev.sales ?? 0) + (row.sales ?? 0),
              units: (prev.units ?? 0) + (row.units ?? 0),
              totalCost: (prev.totalCost ?? 0) + (row.totalCost ?? 0),
              grossProfit: (prev.grossProfit ?? 0) + (row.grossProfit ?? 0),
            });
          }
        }

        const aggregatedTrend = Array.from(byDate.values()).sort((a, b) => a.date.localeCompare(b.date));
        setTrendData(aggregatedTrend);
      } else {
        const p = new URLSearchParams();
        p.set("itemKey", itemKey);
        p.set("startDate", qsStartDate);
        p.set("endDate", qsEndDate);
        const res = (await apiGetJson(
          `/api/profit/profit-trend?${p.toString()}`,
        )) as { data?: RawTrendPoint[] };
        const normalized = normalizeTrendRows(res.data ?? []);
        setTrendData(normalized);
      }
    } catch (e: any) {
      const msg = String(e?.message || "");
      setTrendFetchFailed(true);
      setTrendData([]);
      if (msg.includes("404") && msg.includes("/api/profit/profit-trend")) {
        setTrendFetchMessage("日次推移は現在ご利用いただけません。ご不便をおかけします");
      } else if (msg.includes("401") || msg.includes("UNAUTHORIZED")) {
        setTrendFetchMessage("日次推移の認証に失敗しました。再ログインしてください");
      } else {
        setTrendFetchMessage("日次推移データの取得に失敗しました");
      }
    }
  }, [
    fetchItemAggregateData,
    isItemAggregateMode,
    isRestrictedPlan,
    itemAggregateSkuKeys,
    itemKey,
    qsEndDate,
    qsStartDate,
  ]);

  useEffect(() => {
    fetchTrendData();
  }, [fetchTrendData]);

  const summary = data?.summary ?? {};
  const costBreakdown = data?.costBreakdown ?? {};
  const adDetails = data?.adDetails ?? [];
  const insightText =
    data?.insightText ||
    "利益率と費用構成を確認し、広告費・物流費・販促費の見直し余地をチェックしてください。";

  const refreshItemImageUrl = useCallback(
    async (options?: { silent?: boolean }) => {
      if (!itemKey) return;

      const itemCode = String(
        header.itemManagementNumber ??
          header.itemNumber ??
          itemKey.split("-")[0] ??
          "",
      ).trim();
      if (!itemCode) return;

      const yearMonthBase = String(data?.endDate ?? qsEndDate ?? "").slice(0, 7);
      if (!/^\d{4}-\d{2}$/.test(yearMonthBase)) return;

      setImageRefreshLoading(true);
      try {
        const res = (await apiPutJson("/api/profit/item-image-url/fetch", {
          yearMonth: `${yearMonthBase}-01`,
          itemKey,
          itemCode,
        })) as { imageUrl?: string };

        const fetchedImageUrl = String(res?.imageUrl ?? "").trim();
        if (fetchedImageUrl) {
          setData((prev) => {
            if (!prev) return prev;
            return {
              ...prev,
              header: {
                ...(prev.header ?? {}),
                imageUrl: fetchedImageUrl,
              },
            };
          });
        }

        // URLが同一でも再描画時にブラウザキャッシュを回避する
        setImageCacheBuster(Date.now());
      } catch (e: any) {
        if (!options?.silent) {
          setError(e?.message || "画像の再取得に失敗しました。");
        }
      } finally {
        setImageRefreshLoading(false);
      }
    },
    [
      data?.endDate,
      header.itemManagementNumber,
      header.itemNumber,
      itemKey,
      qsEndDate,
    ],
  );

  const imageDisplayUrl = useMemo(() => {
    const raw = String(header.imageUrl ?? "").trim();
    if (!raw) return "";
    const sep = raw.includes("?") ? "&" : "?";
    return `${raw}${sep}v=${imageCacheBuster}`;
  }, [header.imageUrl, imageCacheBuster]);

  useEffect(() => {
    if (!data || !itemKey) return;

    const currentImageUrl = String(header.imageUrl ?? "").trim();
    if (currentImageUrl) return;

    const itemCode = String(
      header.itemManagementNumber ??
        header.itemNumber ??
        itemKey.split("-")[0] ??
        "",
    ).trim();
    if (!itemCode) return;

    const yearMonthBase = String(data.endDate ?? qsEndDate ?? "").slice(0, 7);
    if (!/^\d{4}-\d{2}$/.test(yearMonthBase)) return;

    const fetchKey = `${itemKey}|${yearMonthBase}|${itemCode}`;
    if (autoImageFetchKeys.has(fetchKey)) return;

    setAutoImageFetchKeys((prev) => {
      const next = new Set(prev);
      next.add(fetchKey);
      return next;
    });

    void refreshItemImageUrl({ silent: true });
  }, [
    autoImageFetchKeys,
    data,
    header.imageUrl,
    header.itemManagementNumber,
    header.itemNumber,
    itemKey,
    qsEndDate,
    refreshItemImageUrl,
  ]);

  const costMissingFlag = num(summary.costMissingFlag ?? data?.costMissingFlag);
  const costSource = String(summary.costSource ?? data?.costSource ?? "");
  const isCostMissing = costMissingFlag > 0;

  const structureUnitCount = useMemo(() => {
    const units = num(summary.units);
    return units > 0 ? units : 0;
  }, [summary.units]);

  // Amazon の費用は「商品ごとの実額（直課）」と「明細に商品内訳が無い費用の売上按分」の
  // 合算。内訳が見えないと実費との差が説明できないため、Amazon のときだけ内訳行を出す。
  const feeDirectAmount = nullableNum(costBreakdown.rakutenFee?.directAmount);
  const feeAllocatedAmount = nullableNum(costBreakdown.rakutenFee?.allocatedAmount);
  const logisticsDirectAmount = nullableNum(costBreakdown.logistics?.directAmount);
  const logisticsAllocatedAmount = nullableNum(costBreakdown.logistics?.allocatedAmount);
  const showAmazonAllocationBreakdown =
    mall === "amazon" && (feeDirectAmount !== null || logisticsDirectAmount !== null);

  // 1個あたりの金額（判断しやすいよう金額の横に添える）
  const perUnit = (amount: null | number) => {
    if (amount === null || structureUnitCount <= 0) return "";
    return `${nf0.format(Math.round(amount / structureUnitCount))}円/個`;
  };

  const renderCostName = (label: string) => (
    <span className="ipd-cost-name">
      <span
        className="ipd-cost-dot"
        style={{ backgroundColor: getStructureBarColor(label, "cost", null) }}
      />
      {label}
    </span>
  );

  const currentUnitCost = useMemo(() => nullableNum(summary.unitCost), [summary.unitCost]);

  const trendChartData = useMemo(() => {
    // 精度優先: 日次利益は推定補完せず、API実測値のみを表示する。
    return trendData;
  }, [trendData]);

  const trendMetricLabels = useMemo<Record<TrendMetricKey, string>>(() => ({
    sales: "売上",
    profitAmount: profitMode === "margin" ? "利益額" : "利益額",
    profitRate: profitMode === "margin" ? "利益率" : "利益率",
    units: "個数",
  }), [profitMode]);

  const trendPresets = useMemo(() => ([
    {
      label: "売上 + 利益額",
      primary: "sales" as TrendMetricKey,
      secondary: "profitAmount" as TrendMetricKey,
      purpose: "売上が増えた日に利益も伴っているかを確認します。売上急増日に利益が伸びていなければ、値引きや販促条件の見直し候補です。",
    },
    {
      label: "売上 + 利益率",
      primary: "sales" as TrendMetricKey,
      secondary: "profitRate" as TrendMetricKey,
      purpose: "売上増と利益率の両立を確認します。売上が伸びても利益率が落ちる日は、コストや値引き負担が重い可能性があります。",
    },
    {
      label: "利益額 + 個数",
      primary: "profitAmount" as TrendMetricKey,
      secondary: "units" as TrendMetricKey,
      purpose: "販売個数が増えた日に利益額も伸びているかを確認します。個数だけ増えて利益が鈍い場合は、単価や費用構成を点検すべきです。",
    },
  ]), []);

  const trendDisplayData = useMemo<TrendDisplayPoint[]>(() => {
    return trendChartData.map((row) => {
      const date = dayjs(row.date);
      const profitAmount = row.grossProfit;
      const profitRate =
        row.sales !== null && row.sales !== 0 && profitAmount !== null
          ? (profitAmount / row.sales) * 100
          : null;
      return {
        ...row,
        label: date.isValid() ? date.format("M/D") : row.date,
        profitAmount,
        profitRate,
        isWeekend: date.day() === 0 || date.day() === 6,
        isSpecialDay: date.date() % 5 === 0,
      };
    });
  }, [trendChartData]);

  const visibleTrendData = useMemo(() => {
    if (trendDisplayData.length === 0) return [];
    if (trendPeriod === "month") return trendDisplayData;
    if (trendPeriod === "2w") return trendDisplayData.slice(-14);
    return trendDisplayData.slice(-30);
  }, [trendDisplayData, trendPeriod]);

  const trendWeekendRanges = useMemo(() => {
    const ranges: Array<{ x1: string; x2: string }> = [];
    let start: string | null = null;

    visibleTrendData.forEach((row, index) => {
      if (row.isWeekend && start === null) start = row.date;

      const next = visibleTrendData[index + 1];
      if (start !== null && (!next || !next.isWeekend)) {
        ranges.push({ x1: start, x2: row.date });
        start = null;
      }
    });

    return ranges;
  }, [visibleTrendData]);

  const currentTrendPurpose =
    activeTrendPreset !== null ? trendPresets[activeTrendPreset]?.purpose ?? null : null;

  const trendSummaryItems = useMemo(() => {
    const buildSummary = (metric: TrendMetricKey, includeWoW: boolean) => {
      const points = visibleTrendData
        .map((row) => row[metric])
        .filter((value): value is number => value !== null && Number.isFinite(value));
      if (!points.length) return [];
      const total = points.reduce((sum, value) => sum + value, 0);
      const average = total / points.length;
      const max = Math.max(...points);
      const maxRow = visibleTrendData.find((row) => row[metric] === max);
      const summaries = [
        {
          label: `${trendMetricLabels[metric]} 期間合計`,
          value: fmtTrendValue(metric, total),
          className: "",
        },
        {
          label: `${trendMetricLabels[metric]} 日平均`,
          value: fmtTrendValue(metric, average),
          className: "",
        },
        {
          label: `${trendMetricLabels[metric]} 最大${maxRow ? `（${maxRow.label}）` : ""}`,
          value: fmtTrendValue(metric, max),
          className: "pos",
        },
      ];

      if (includeWoW && points.length >= 8) {
        const latest = points[points.length - 1];
        const prev = points[points.length - 8];
        const change = prev !== 0 ? ((latest - prev) / prev) * 100 : null;
        summaries.push({
          label: `${trendMetricLabels[metric]} 前週比`,
          value: change === null ? "—" : `${change >= 0 ? "+" : ""}${change.toFixed(1)}%`,
          className: change === null ? "" : change >= 0 ? "pos" : "neg",
        });
      }

      return summaries;
    };

    return [
      ...buildSummary(trendPrimaryMetric, true),
      ...(trendSecondaryMetric ? buildSummary(trendSecondaryMetric, false).slice(0, 3) : []),
    ];
  }, [visibleTrendData, trendMetricLabels, trendPrimaryMetric, trendSecondaryMetric]);

  const handleTrendPreset = useCallback((index: number) => {
    const preset = trendPresets[index];
    setActiveTrendPreset(index);
    setTrendPrimaryMetric(preset.primary);
    setTrendSecondaryMetric(preset.secondary);
  }, [trendPresets]);

  const handleTrendSingleMetric = useCallback((metric: TrendMetricKey) => {
    setActiveTrendPreset(null);
    setTrendPrimaryMetric(metric);
    setTrendSecondaryMetric(null);
  }, []);

  const unitSales = useMemo(() => {
    const units = num(summary.units);
    const salesAfterCoupon = nullableNum(summary.salesAfterCoupon);
    if (units <= 0 || salesAfterCoupon === null) return null;
    return salesAfterCoupon / units;
  }, [summary.salesAfterCoupon, summary.units]);

  // 一覧SQLと同じ売上（店舗カルテのSKU別売上。送料別の商品はお客様が払った送料を含む）。
  // クーポン値引き「前」の金額であり、クーポンは変動費側で控除する。比率もこの金額を100%とする（一覧の利益率と分母を揃えるため）
  const salesValue = useMemo(() => {
    return nullableNum(summary.salesAfterCoupon);
  }, [summary.salesAfterCoupon]);

  // 送料別の商品は、お客様が払った送料を売上に含めている（楽天のみ）
  const isSeparateShipping = mall === "rakuten" && num(summary.hasSeparateShipping) > 0;
  const shippingRevenueAmount = num(summary.shippingRevenueAmount);
  // 価格シミュレーターの「販売価格」は商品の本体価格（送料を除く）。送料別の商品は1個あたりの送料を別に足して試算する
  const shippingPerUnit =
    isSeparateShipping && num(summary.units) > 0 ? shippingRevenueAmount / num(summary.units) : 0;
  const simBasePrice = unitSales === null ? null : unitSales - shippingPerUnit;

  const breakdownRppAmount = useMemo(() => {
    const rawRpp = nullableNum(costBreakdown.rpp?.amount);
    if (!isItemAggregateMode) {
      return rawRpp;
    }
    return rawRpp === null ? 0 : rawRpp;
  }, [isItemAggregateMode, costBreakdown.rpp?.amount]);

  const breakdownRakutenFeeAmount = useMemo(() => {
    const rawFee = nullableNum(costBreakdown.rakutenFee?.amount);
    if (rawFee !== null) return rawFee;
    return isItemAggregateMode ? 0 : null;
  }, [isItemAggregateMode, costBreakdown.rakutenFee?.amount]);

  const breakdownRakutenFeeRate = useMemo(() => {
    if (breakdownRakutenFeeAmount === null || salesValue === null || salesValue <= 0) {
      return null;
    }
    return breakdownRakutenFeeAmount / salesValue;
  }, [breakdownRakutenFeeAmount, salesValue]);

  const breakdownRppRate = useMemo(() => {
    if (breakdownRppAmount === null || salesValue === null || salesValue <= 0) {
      return null;
    }
    return breakdownRppAmount / salesValue;
  }, [breakdownRppAmount, salesValue]);

  const breakdownCogsAmount = useMemo(() => {
    const v = nullableNum(costBreakdown.cogs?.amount);
    return v ?? 0;
  }, [costBreakdown.cogs?.amount]);

  const breakdownLogisticsAmount = useMemo(() => {
    const v = nullableNum(costBreakdown.logistics?.amount);
    return v ?? 0;
  }, [costBreakdown.logistics?.amount]);

  const breakdownPointAmount = useMemo(() => {
    const v = nullableNum(costBreakdown.point?.amount);
    return v ?? 0;
  }, [costBreakdown.point?.amount]);

  const breakdownAffiliateAmount = useMemo(() => {
    const v = nullableNum(costBreakdown.affiliate?.amount);
    return v ?? 0;
  }, [costBreakdown.affiliate?.amount]);

  const breakdownCouponAmount = useMemo(() => {
    const v = nullableNum(costBreakdown.coupon?.amount);
    return v ?? 0;
  }, [costBreakdown.coupon?.amount]);

  const breakdownRppexpAmount = useMemo(() => {
    const v = nullableNum(costBreakdown.rppexp?.amount);
    return v ?? 0;
  }, [costBreakdown.rppexp?.amount]);

  const breakdownOtherAdsAmount = useMemo(() => {
    const v = nullableNum(costBreakdown.otherAds?.amount);
    return v ?? 0;
  }, [costBreakdown.otherAds?.amount]);

  const adSubtotalAmount = useMemo(() => {
    return sumNumbers([
      breakdownRppAmount,
      breakdownRppexpAmount,
      breakdownOtherAdsAmount,
    ]);
  }, [breakdownRppAmount, breakdownRppexpAmount, breakdownOtherAdsAmount]);

  // クーポンは一覧SQLでは費用合計に含まれている。詳細だけ売上控除のつもりで扱っていたが、
  // 一覧が使う売上（店舗カルテのSKU別売上）はクーポン値引き「前」の金額なので、
  // 詳細ではクーポンがどこにも引かれず、利益が値引き額ぶん過大になっていた。
  // 一覧と同じく販促費の一部として費用計上する
  const promotionSubtotalAmount = useMemo(() => {
    return sumNumbers([
      breakdownPointAmount,
      breakdownAffiliateAmount,
      breakdownCouponAmount,
      adSubtotalAmount,
    ]);
  }, [breakdownPointAmount, breakdownAffiliateAmount, breakdownCouponAmount, adSubtotalAmount]);

  const variableCostSubtotalAmount = useMemo(() => {
    return sumNumbers([
      breakdownCogsAmount,
      breakdownRakutenFeeAmount,
      breakdownLogisticsAmount,
      promotionSubtotalAmount,
    ]);
  }, [
    breakdownCogsAmount,
    breakdownRakutenFeeAmount,
    breakdownLogisticsAmount,
    promotionSubtotalAmount,
  ]);

  const fixedCostForPl = useMemo(() => {
    const v = nullableNum(costBreakdown.fixed?.amount);
    return v ?? 0;
  }, [costBreakdown.fixed?.amount]);

  const displayCostTotalAmount = useMemo(() => {
    return profitMode === "operating"
      ? variableCostSubtotalAmount + fixedCostForPl
      : variableCostSubtotalAmount;
  }, [profitMode, variableCostSubtotalAmount, fixedCostForPl]);

  const displayProfit = useMemo(() => {
    if (salesValue === null) return null;
    return salesValue - displayCostTotalAmount;
  }, [salesValue, displayCostTotalAmount]);

  const displayRate = useMemo(() => {
    if (salesValue === null || salesValue <= 0 || displayProfit === null) return null;
    return displayProfit / salesValue;
  }, [salesValue, displayProfit]);

  const unitProfit = useMemo(() => {
    const units = num(summary.units);
    if (units <= 0 || displayProfit === null) return null;
    return displayProfit / units;
  }, [summary.units, displayProfit]);

  const unitTotalCost = useMemo(() => {
    const units = num(summary.units);
    if (units <= 0) return null;
    return displayCostTotalAmount / units;
  }, [summary.units, displayCostTotalAmount]);

  const variableCostRate = useMemo(() => {
    if (salesValue === null || salesValue <= 0) return null;
    return variableCostSubtotalAmount / salesValue;
  }, [salesValue, variableCostSubtotalAmount]);

  const unitOtherVariableCost = useMemo(() => {
    if (unitSales === null || currentUnitCost === null || unitProfit === null) return null;
    return Math.max(0, unitSales - currentUnitCost - unitProfit);
  }, [unitSales, currentUnitCost, unitProfit]);

  const unitProfitStructure = useMemo(() => {
    if (structureUnitCount <= 0 || unitSales === null) return [];

    const unitRakutenFee = (breakdownRakutenFeeAmount ?? 0) / structureUnitCount;
    const unitLogistics = breakdownLogisticsAmount / structureUnitCount;
    const unitPromotion = promotionSubtotalAmount / structureUnitCount;
    const unitCogs = breakdownCogsAmount / structureUnitCount;
    const unitFixed = fixedCostForPl / structureUnitCount;

    const rows: ProfitStructureRow[] = [
      { label: "売上", amount: unitSales, type: "base", colorKey: "blue" },
      { label: "原価", amount: -unitCogs, type: "cost" },
      { label: feeLabel, amount: -unitRakutenFee, type: "cost" },
      { label: "物流費", amount: -unitLogistics, type: "cost" },
      { label: "販促費", amount: -unitPromotion, type: "cost" },
    ];

    if (profitMode === "operating") {
      rows.push({ label: "固定費", amount: -unitFixed, type: "cost" });
    }

    rows.push({
      label: profitMode === "margin" ? "限界利益" : "営業利益",
      amount: unitProfit,
      type: "profit",
      colorKey: "green",
    });

    return rows;
  }, [
    structureUnitCount,
    unitSales,
    breakdownRakutenFeeAmount,
    breakdownLogisticsAmount,
    promotionSubtotalAmount,
    breakdownCogsAmount,
    fixedCostForPl,
    profitMode,
    unitProfit,
  ]);

  const waterfallRows = useMemo(() => {
    if (!unitProfitStructure.length) return [];

    const rawAmounts = unitProfitStructure.map((row) => nullableNum(row.amount));
    const firstAmount = rawAmounts.find((v) => v !== null) ?? 0;
    let running = firstAmount;
    let maxScaleValue = Math.abs(firstAmount);

    const positioned = unitProfitStructure.map((row, idx) => {
      const amount = rawAmounts[idx];

      if (idx === 0) {
        const widthValue = Math.abs(amount ?? 0);
        maxScaleValue = Math.max(maxScaleValue, widthValue);
        return {
          ...row,
          amount,
          leftValue: 0,
          widthValue,
        };
      }

      if (amount === null) {
        return {
          ...row,
          amount: null,
          leftValue: 0,
          widthValue: 0,
        };
      }

      if (row.type === "profit") {
        const widthValue = Math.abs(amount);
        const leftValue = 0;
        maxScaleValue = Math.max(maxScaleValue, widthValue);
        return {
          ...row,
          amount,
          leftValue,
          widthValue,
        };
      }

      const prevRunning = running;
      running += amount;

      const leftValue = Math.max(Math.min(prevRunning, running), 0);
      const widthValue = Math.abs(amount);

      maxScaleValue = Math.max(maxScaleValue, Math.abs(prevRunning), Math.abs(running));

      return {
        ...row,
        amount,
        leftValue,
        widthValue,
      };
    });

    const scale = maxScaleValue > 0 ? maxScaleValue : 1;

    return positioned.map((row) => ({
      ...row,
      leftPct: (row.leftValue / scale) * 100,
      widthPct: row.widthValue <= 0 ? 0 : Math.max((row.widthValue / scale) * 100, 2),
    }));
  }, [unitProfitStructure]);

  // 損益分岐点（固定費 ÷ 1個あたり貢献利益）
  const fixedCostAmount = useMemo(() => {
    const fixed = nullableNum(costBreakdown.fixed?.amount);
    if (fixed === null) return null;
    return Math.max(0, fixed);
  }, [costBreakdown.fixed?.amount]);

  const unitContribution = useMemo(() => {
    const units = num(summary.units);
    if (units <= 0 || salesValue === null) return null;
    const marginProfit = salesValue - variableCostSubtotalAmount;
    return marginProfit / units;
  }, [summary.units, salesValue, variableCostSubtotalAmount]);

  const unitVariableCost = useMemo(() => {
    if (unitSales === null || unitContribution === null) return null;
    return Math.max(0, unitSales - unitContribution);
  }, [unitSales, unitContribution]);

  const bepUnits = useMemo(() => {
    if (fixedCostAmount === null) return null;
    if (fixedCostAmount <= 0) return 0;
    if (unitContribution === null || unitContribution <= 0) return null;
    return Math.ceil(fixedCostAmount / unitContribution);
  }, [fixedCostAmount, unitContribution]);

  const bepSales = useMemo(() => {
    if (bepUnits === null || unitSales === null) return null;
    return bepUnits * unitSales;
  }, [bepUnits, unitSales]);

  const bepAchievementRate = useMemo(() => {
    if (bepUnits === null) return null;
    if (bepUnits <= 0) return 1;
    return num(summary.units) / bepUnits;
  }, [bepUnits, summary.units]);

  // 価格シミュレーター
  const simResult = useMemo(() => {
    const sp = nullableNum(simPrice);
    const sc = simCost === "" ? currentUnitCost : nullableNum(simCost);
    const currentUnits = num(summary.units);
    const su = simUnits ? (nullableNum(simUnits) ?? currentUnits) : currentUnits;
    if (sp === null || sp <= 0 || su <= 0) return null;
    if (sc === null || unitOtherVariableCost === null) return null;
    // 1個あたりの売上＝入力した本体価格＋お客様が払う送料（送料別の商品のみ。それ以外は0）
    const simUnitSales = sp + shippingPerUnit;
    const simMarginUnit = simUnitSales - sc - unitOtherVariableCost;
    const simTotalMargin = simMarginUnit * su;
    const simMarginRate = simUnitSales > 0 ? simMarginUnit / simUnitSales : null;
    const fixedCost = nullableNum(costBreakdown.fixed?.amount);
    const simOpProfit = fixedCost !== null ? simTotalMargin - fixedCost : null;
    const simSales = simUnitSales * su;
    const simOpRate =
      simOpProfit !== null && simSales > 0 ? simOpProfit / simSales : null;
    const simDisplayUnitProfit =
      profitMode === "margin"
        ? simMarginUnit
        : simOpProfit !== null
        ? simOpProfit / su
        : null;
    const simDisplayTotalProfit = profitMode === "margin" ? simTotalMargin : simOpProfit;
    const simDisplayRate = profitMode === "margin" ? simMarginRate : simOpRate;

    return {
      simSales,
      simMarginUnit,
      simTotalMargin,
      simMarginRate,
      simOpProfit,
      simOpRate,
      simDisplayUnitProfit,
      simDisplayTotalProfit,
      simDisplayRate,
      simCost: sc,
      simUnits: su,
    };
  }, [
    simPrice,
    simCost,
    simUnits,
    summary.units,
    currentUnitCost,
    unitOtherVariableCost,
    costBreakdown.fixed?.amount,
    profitMode,
    shippingPerUnit,
  ]);

  const simBase = useMemo(() => {
    const currentUnits = num(summary.units);
    const baseSales = nullableNum(summary.salesAfterCoupon);
    const baseProfit = displayProfit;
    const baseRate =
      baseSales !== null && baseSales > 0 && displayRate !== null ? displayRate : null;
    return {
      currentUnits,
      baseSales,
      baseProfit,
      baseRate,
    };
  }, [summary.units, summary.salesAfterCoupon, displayProfit, displayRate]);

  const couponSummaryCards = useMemo(() => {
    const source = couponReferenceData ?? couponData;
    if (!source) return [];
    const totalOrders = source.store.totalOrderCount + source.rakuten.totalOrderCount;
    const totalDiscount = source.store.totalDiscount + source.rakuten.totalDiscount;
    const storeAvg = source.store.totalOrderCount > 0
      ? source.store.totalDiscount / source.store.totalOrderCount
      : null;
    return [
      {
        label: "店舗発行クーポン 値引き総額",
        value: fmtYen(-source.store.totalDiscount),
        sub: `${nf0.format(source.store.totalOrderCount)}件利用`,
        tone: "neg",
      },
      {
        label: "クーポン利用総件数",
        value: `${nf0.format(totalOrders)}件`,
        sub: `全体値引き ${fmtYen(-totalDiscount)}`,
        tone: "",
      },
      {
        label: "店舗発行 平均値引き額",
        value: storeAvg === null ? "—" : fmtYen(-storeAvg),
        sub: "1件あたり",
        tone: "pos",
      },
    ];
  }, [couponData, couponReferenceData]);

  const couponDetailDisplayData = useMemo(() => {
    return couponReferenceData ?? couponData;
  }, [couponReferenceData, couponData]);

  const couponAmountDisplay = useMemo(() => {
    const summaryCoupon = nullableNum(summary.couponAmount);
    if (summaryCoupon !== null) return summaryCoupon;
    const couponFromBreakdown = nullableNum(costBreakdown.coupon?.amount);
    if (couponFromBreakdown !== null) return couponFromBreakdown;
    if (!couponDetailDisplayData) return null;
    return num(couponDetailDisplayData.store.totalDiscount) + num(couponDetailDisplayData.rakuten.totalDiscount);
  }, [summary.couponAmount, costBreakdown.coupon?.amount, couponDetailDisplayData]);

  // 店舗カルテの売上は既にクーポン値引き前の金額なので、そのまま使う。
  // 以前はここでさらにクーポンを足しており、値引き前売上が1回分過大に表示されていた
  const grossSalesBeforeCouponDisplay = useMemo(() => {
    return salesValue;
  }, [salesValue]);

  const couponRateDisplay = useMemo(() => {
    const summaryRate = nullableNum(summary.couponRate);
    if (summaryRate !== null) return summaryRate;
    if (grossSalesBeforeCouponDisplay === null || grossSalesBeforeCouponDisplay <= 0 || couponAmountDisplay === null) {
      return null;
    }
    return couponAmountDisplay / grossSalesBeforeCouponDisplay;
  }, [summary.couponRate, grossSalesBeforeCouponDisplay, couponAmountDisplay]);

  const couponReferenceSummaryCards = useMemo(() => {
    if (!couponReferenceData) return [];

    const totalOrders = couponReferenceData.store.totalOrderCount + couponReferenceData.rakuten.totalOrderCount;
    const totalDiscount = couponReferenceData.store.totalDiscount + couponReferenceData.rakuten.totalDiscount;

    return [
      {
        label: "参考値 商品別クーポン値引き総額",
        value: fmtYen(-totalDiscount),
        sub: `${nf0.format(totalOrders)}件の注文を注文内売上比で配賦`,
        tone: "neg",
      },
      {
        label: "参考値 対象注文件数",
        value: `${nf0.format(totalOrders)}件`,
        sub: "対象商品を含む注文",
        tone: "",
      },
      {
        label: "参考値 配賦方法",
        value: "注文内売上比",
        sub: "対象注文の中で売上比配賦",
        tone: "pos",
      },
    ];
  }, [couponReferenceData]);

  const backToList = useCallback(() => {
    const basePrefix = location.pathname.startsWith("/demo") ? "/demo" : "/app";
    const p = new URLSearchParams();
    p.set("mode", mode);
    p.set("profitMode", backProfitMode);
    p.set("startDate", qsStartDate);
    p.set("endDate", qsEndDate);
    navigate(`${basePrefix}/itemProfitSummary?${p.toString()}`);
  }, [location.pathname, mode, backProfitMode, qsStartDate, qsEndDate, navigate]);

  const displayProfitClass =
    displayProfit === null ? "" : displayProfit < 0 ? "neg" : "pos";
  const displayRateClass =
    displayRate === null ? "" : displayRate < 0 ? "neg" : "pos";
  const unitProfitClass =
    unitProfit === null ? "" : unitProfit < 0 ? "neg" : "pos";
  const plProfitLabel = profitMode === "margin" ? "限界利益" : "営業利益";
  const unitProfitLabel = profitMode === "margin" ? "1個あたり限界利益" : "1個あたり営業利益";
  const couponOrderCount = useMemo(() => {
    if (!couponDetailDisplayData) return null;
    return num(couponDetailDisplayData.store.totalOrderCount) + num(couponDetailDisplayData.rakuten.totalOrderCount);
  }, [couponDetailDisplayData]);
  const kpiOrderCount = couponOrderCount ?? num(summary.orderCount);
  const useSharedScaleMode = trendPrimaryMetric === "sales" && trendSecondaryMetric === "profitAmount";

  return (
    <div className="ipd-page">
      {loading && (
        <div className="ipd-loading">
          <LoadingInline />
        </div>
      )}

      <ItemProfitUpgradeDialog
        open={showUpgradeDialog}
        onClose={() => setShowUpgradeDialog(false)}
      />

      <div className={isRestrictedPlan ? "pointer-events-none select-none opacity-50" : ""}>

      <button className="ipd-back-btn" type="button" onClick={backToList}>
        ← 一覧に戻る
      </button>

      <div className="ipd-breadcrumb">
        <button className="ipd-breadcrumb-link" type="button" onClick={backToList}>
          商品別利益
        </button>
        <span className="ipd-breadcrumb-sep">›</span>
        <span>{header.itemManagementNumber || itemKey || "詳細"}</span>
      </div>

      {error && <div className="ipd-error">{error}</div>}

      <div className="ipd-header-card">
        <div className="ipd-header-main">
          {imageDisplayUrl ? (
            <img
              src={imageDisplayUrl}
              className="ipd-header-thumb"
              alt=""
              onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
            />
          ) : header.genreName ? (
            <div
              className="ipd-header-thumb-fallback"
              style={{ background: GENRE_COLORS[header.genreName] ?? "#888" }}
            >
              {header.genreName[0]}
            </div>
          ) : null}
          <div className="ipd-header-text">
            <div className="ipd-title-row">
              <div className="ipd-title">{header.itemName || "商品別利益詳細"}</div>
              {header.genreName ? (
                <span className="ipd-genre-badge">{header.genreName}</span>
              ) : null}
              {isSeparateShipping ? (
                <span className="ipd-ship-badge">送料別</span>
              ) : null}
              {String(header.imageUrl ?? "").trim() ? (
                <button
                  type="button"
                  className="ipd-image-refresh-btn"
                  onClick={() => void refreshItemImageUrl()}
                  disabled={imageRefreshLoading}
                >
                  {imageRefreshLoading ? "画像更新中..." : "画像を再取得"}
                </button>
              ) : null}
            </div>

            <div className="ipd-meta">
              商品管理番号：{header.itemManagementNumber || "—"}　｜　商品番号：
              {header.itemNumber || "—"}
              <>　｜　SKU管理番号：{header.skuManagementNumber || "—"}</>
              <>　｜　SKU番号：{header.skuNumber || "—"}</>
            </div>

            <div className="ipd-range-label">
              {header.rangeLabel || `${qsStartDate} 〜 ${qsEndDate}`}
            </div>
          </div>
        </div>

        <div className="ipd-mode-bar">
          <span className="ipd-mode-label">表示モード：</span>

          <div className="ipd-mode-toggle">
            <button
              className={`ipd-mode-btn ${profitMode === "margin" ? "active" : ""}`}
              type="button"
              onClick={() => setProfitMode("margin")}
            >
              限界利益モード
            </button>
            <button
              className={`ipd-mode-btn ${profitMode === "operating" ? "active" : ""}`}
              type="button"
              onClick={() => setProfitMode("operating")}
            >
              営業利益モード
            </button>
          </div>

          <span
            className={`ipd-mode-tag ${
              profitMode === "margin" ? "margin" : "operating"
            }`}
          >
            {profitMode === "margin" ? "限界利益表示中" : "営業利益表示中"}
          </span>

          <span className={`ipd-source-chip ${isCostMissing ? "missing" : "ok"}`}>
            原価状態: {
              isCostMissing
                ? "未設定"
                : costSource === "avg_rate"
                ? "平均原価率適用"
                : "商品原価適用"
            }
          </span>
        </div>
      </div>

      <div className="ipd-metrics">
        <div className="ipd-metric">
          <div className="ipd-metric-label"><span className="ipd-step-badge">1</span>売上</div>
          <div className="ipd-metric-value">{fmtYen(salesValue)}</div>
          <div className="ipd-metric-sub">クーポン値引き前の金額</div>
          {isSeparateShipping ? (
            <div className="ipd-metric-sub">うち送料（お客様負担） {fmtYen(shippingRevenueAmount)}</div>
          ) : null}
          <div className="ipd-metric-sub">クーポン値引き {fmtYen(breakdownCouponAmount === 0 ? null : -breakdownCouponAmount)}（値引率 {fmtPct(couponRateDisplay)}）は変動費に計上</div>
          <div className="ipd-metric-sub">販売 {nf0.format(num(summary.units))}個 / {nf0.format(kpiOrderCount)}件</div>
        </div>

        <div className="ipd-metric">
          <div className="ipd-metric-label"><span className="ipd-step-badge">2</span>変動費合計</div>
          <div className="ipd-metric-value neg">{fmtYen(-variableCostSubtotalAmount)}</div>
          <div className="ipd-metric-sub">売上比 {fmtPct(variableCostRate)}</div>
          {profitMode === "operating" && (
            <div className="ipd-metric-sub">固定費込み総費用 {fmtYen(displayCostTotalAmount)}</div>
          )}
        </div>

        <div className="ipd-metric">
          <div className="ipd-metric-label"><span className="ipd-step-badge">3</span>{plProfitLabel}（①−②{profitMode === "operating" ? "−固定費" : ""}）</div>
          <div className={`ipd-metric-value ${displayProfitClass}`}>{fmtYen(displayProfit)}</div>
          {(() => {
            const rate = profitMode === "margin"
              ? summary.momMarginProfitChangeRate
              : summary.momOpProfitChangeRate;
            const r = nullableNum(rate);
            if (r === null) return null;
            const sign = r >= 0 ? "+" : "";
            return (
              <div className={`ipd-mom-badge ${r >= 0 ? "pos" : "neg"}`}>
                {`前月同期比 ${sign}${(r * 100).toFixed(1)}%`}
              </div>
            );
          })()}
        </div>

        <div className="ipd-metric">
          <div className="ipd-metric-label"><span className="ipd-step-badge">4</span>{plProfitLabel}率</div>
          <div className={`ipd-metric-value ${displayRateClass}`}>{fmtPct(displayRate)}</div>
          <div className="ipd-metric-sub">売上比（①基準）</div>
        </div>

        <div className="ipd-metric">
          <div className="ipd-metric-label">{unitProfitLabel}</div>
          <div className={`ipd-metric-value ${unitProfitClass}`}>{fmtYen(unitProfit)}</div>
          <div className="ipd-metric-sub">売上/個 {fmtYen(unitSales)} / 変動費/個 {fmtYen(num(summary.units) > 0 ? variableCostSubtotalAmount / num(summary.units) : null)}</div>
          {profitMode === "operating" ? (
            <div className="ipd-metric-sub">総費用/個 {fmtYen(unitTotalCost)}</div>
          ) : (
            <div className="ipd-metric-sub">原価/個 {fmtYen(summary.unitCost)}</div>
          )}
        </div>
      </div>

      <div className="ipd-note-box" role="note" aria-label="計算式の注釈">
        <span className="ipd-note-icon">i</span>
        <div>
          {profitMode === "margin"
            ? "限界利益 = ①売上 - ②変動費合計（クーポン値引きは変動費に含めます。売上は値引き前の金額です）"
            : "営業利益 = ①売上 - ②変動費合計 - 固定費（クーポン値引きは変動費に含めます）"}
          {isCostMissing ? "。原価未設定の商品を含むため参考値です。" : "。"}
        </div>
      </div>

      {/* 損益分岐点 */}
      {
        (() => {
          const canComputeBep =
            fixedCostAmount !== null &&
            unitContribution !== null &&
            unitContribution > 0 &&
            bepUnits !== null;
          const statusClass = canComputeBep && (bepAchievementRate ?? 0) >= 1 ? "pos" : "neg";
          const statusText =
            fixedCostAmount === null
              ? "固定費が未設定のため算出できません"
              : unitContribution === null || unitContribution <= 0
              ? "貢献利益が0以下のため算出できません"
              : (bepAchievementRate ?? 0) >= 1
              ? `今月達成済み（${nf0.format(num(summary.units))}個）`
              : `達成まで ${bepUnits !== null ? Math.max(bepUnits - num(summary.units), 0) : "—"}個`;

          return (
        <div className="ipd-bep-card">
          <div className="ipd-bep-title">損益分岐点</div>
          <div className="ipd-bep-box">
            <div className="ipd-bep-main-row">
              <div className="ipd-bep-icon">📊</div>
              <div className="ipd-bep-text">
                <div className="ipd-bep-mini-title">月の固定費を回収するために必要な最低販売個数</div>
                <div className="ipd-bep-val-row">
                  <span className="ipd-bep-main-value">{bepUnits !== null ? nf0.format(bepUnits) : "—"}</span>
                  <span className="ipd-bep-unit">個</span>
                  <span className={`ipd-bep-status ${statusClass}`}>
                    {statusText}
                  </span>
                </div>
              </div>
            </div>
            <div className="ipd-bep-progress-wrap">
              <div className="ipd-bep-progress-labels">
                <span>損益分岐点 {bepUnits !== null ? `${nf0.format(bepUnits)}個` : "—"}</span>
                <span>今月 {nf0.format(num(summary.units))}個（{fmtPct(bepAchievementRate)}）</span>
              </div>
              <div className="ipd-bep-bar-wrap">
                <div
                  className="ipd-bep-bar-fill"
                  style={{
                    width: `${Math.min(100, Math.max(0, (bepAchievementRate ?? 0) * 100))}%`,
                    background: canComputeBep
                      ? (bepAchievementRate ?? 0) >= 1
                        ? "#1D9E75"
                        : "#EF9F27"
                      : "#b5d4f4",
                  }}
                />
              </div>
            </div>
            <button
              type="button"
              className="ipd-bep-toggle"
              onClick={() => setBepDetailOpen((prev) => !prev)}
            >
              {bepDetailOpen ? "詳細を閉じる▲" : "詳細を見る▼"}
            </button>
            {bepDetailOpen && (
              <div className="ipd-bep-detail-grid">
                <div className="ipd-bep-stat-card">
                  <div className="ipd-bep-stat-label">分岐点売上</div>
                  <div className="ipd-bep-stat-value">{fmtYen(bepSales)}</div>
                </div>
                <div className="ipd-bep-stat-card">
                  <div className="ipd-bep-stat-label">1個あたり変動費</div>
                  <div className="ipd-bep-stat-value">{fmtYen(unitVariableCost)}</div>
                </div>
                <div className="ipd-bep-stat-card">
                  <div className="ipd-bep-stat-label">1個あたり貢献利益</div>
                  <div className="ipd-bep-stat-value">{fmtYen(unitContribution)}</div>
                </div>
                <div className="ipd-bep-stat-card">
                  <div className="ipd-bep-stat-label">固定費按分</div>
                  <div className="ipd-bep-stat-value">{fmtYen(fixedCostAmount)}</div>
                </div>
              </div>
            )}
          </div>
        </div>
          );
        })()
      }

      <div className="ipd-two-col">
        <div className="ipd-card">
          <div className="ipd-card-title">商品収支（{header.rangeLabel || `${qsStartDate} 〜 ${qsEndDate}`}）</div>
          <div className="ipd-pl-subtitle">比率は売上（クーポン値引き前） = 100%</div>

          <div className="ipd-cost-group">
            <div className="ipd-cost-group-label">売上</div>
            <div className="ipd-cost-row ipd-pl-highlight">
              <span className="ipd-pl-title"><span className="ipd-step-badge">1</span>売上（クーポン値引き前）</span>
              <span>
                <strong>{fmtYen(salesValue)}</strong>
                <small>{nf0.format(num(summary.units))}個 / 100%</small>
              </span>
            </div>
            {isSeparateShipping ? (
              <div className="ipd-cost-row ipd-pl-indent-1">
                <span className="ipd-pl-title">うち送料（お客様負担）</span>
                <span>
                  <strong>{fmtYen(shippingRevenueAmount)}</strong>
                  <small>{fmtPct(salesValue && salesValue > 0 ? shippingRevenueAmount / salesValue : null)}</small>
                </span>
              </div>
            ) : null}
          </div>

          <div className="ipd-cost-group">
            <div className="ipd-cost-group-label">変動費</div>
            <div className="ipd-cost-row ipd-pl-subtotal">
              <span className="ipd-pl-title"><span className="ipd-step-badge">2</span>変動費合計</span>
              <span>
                <strong className="neg">{fmtYen(-variableCostSubtotalAmount)}</strong>
                <small>{fmtPct(variableCostRate)}</small>
              </span>
            </div>

            <div className="ipd-cost-row ipd-pl-indent-1">
              {renderCostName("原価")}
              <span>
                <strong>{fmtYen(breakdownCogsAmount)}</strong>
                <small>{fmtPct(salesValue && salesValue > 0 ? breakdownCogsAmount / salesValue : null)}</small>
              </span>
            </div>

            <div className="ipd-cost-row ipd-pl-indent-1">
              {renderCostName(feeLabel)}
              <span>
                <strong>{fmtYen(breakdownRakutenFeeAmount)}</strong>
                <small>{fmtPct(breakdownRakutenFeeRate)}</small>
              </span>
            </div>

            {/* Amazon のみ: 商品に直接かかった実額と、店舗共通費用の売上按分を分けて示す。
                内訳が分からないと「実費と違う」と見えてしまうため（元永食品様の指摘） */}
            {showAmazonAllocationBreakdown && (
              <>
                <div className="ipd-cost-row ipd-pl-indent-2 ipd-alloc-sub">
                  <span>うち この商品の実費</span>
                  <span>
                    <strong>{fmtYen(feeDirectAmount)}</strong>
                    <small>{perUnit(feeDirectAmount)}</small>
                  </span>
                </div>
                <div className="ipd-cost-row ipd-pl-indent-2 ipd-alloc-sub">
                  <span>うち 店舗共通費用の配分</span>
                  <span>
                    <strong>{fmtYen(feeAllocatedAmount)}</strong>
                    <small>{perUnit(feeAllocatedAmount)}</small>
                  </span>
                </div>
              </>
            )}

            <div className="ipd-cost-row ipd-pl-indent-1">
              {renderCostName("物流費")}
              <span>
                <strong>{fmtYen(breakdownLogisticsAmount)}</strong>
                <small>{fmtPct(salesValue && salesValue > 0 ? breakdownLogisticsAmount / salesValue : null)}</small>
              </span>
            </div>

            {showAmazonAllocationBreakdown && (
              <>
                <div className="ipd-cost-row ipd-pl-indent-2 ipd-alloc-sub">
                  <span>うち この商品の実費（FBA配送料など）</span>
                  <span>
                    <strong>{fmtYen(logisticsDirectAmount)}</strong>
                    <small>{perUnit(logisticsDirectAmount)}</small>
                  </span>
                </div>
                <div className="ipd-cost-row ipd-pl-indent-2 ipd-alloc-sub">
                  <span>うち 店舗共通費用の配分（FBA保管料など）</span>
                  <span>
                    <strong>{fmtYen(logisticsAllocatedAmount)}</strong>
                    <small>{perUnit(logisticsAllocatedAmount)}</small>
                  </span>
                </div>
                <p className="ipd-alloc-note">
                  ※「店舗共通費用の配分」は、Amazonの明細で商品ごとの内訳が提供されない費用（FBA保管料・Amazon
                  Vine費用・FBA返送手数料・大口出品の月額登録料・セール参加費など）を、売上構成比で各商品に割り当てたものです。
                </p>
              </>
            )}

            <div className="ipd-cost-row ipd-pl-indent-1 ipd-pl-subtotal">
              <span>販促費 小計</span>
              <span>
                <strong>{fmtYen(promotionSubtotalAmount)}</strong>
                <small>{fmtPct(salesValue && salesValue > 0 ? promotionSubtotalAmount / salesValue : null)}</small>
              </span>
            </div>

            <div className="ipd-cost-row ipd-pl-indent-2">
              {renderCostName("ポイント費用")}
              <span>
                <strong>{fmtYen(breakdownPointAmount)}</strong>
                <small>{fmtPct(salesValue && salesValue > 0 ? breakdownPointAmount / salesValue : null)}</small>
              </span>
            </div>

            <div className="ipd-cost-row ipd-pl-indent-2">
              {renderCostName("アフィリエイト")}
              <span>
                <strong>{fmtYen(breakdownAffiliateAmount)}</strong>
                <small>{fmtPct(salesValue && salesValue > 0 ? breakdownAffiliateAmount / salesValue : null)}</small>
              </span>
            </div>

            <div className="ipd-cost-row ipd-pl-indent-2">
              {renderCostName("クーポン値引き")}
              <span>
                <strong>{fmtYen(breakdownCouponAmount)}</strong>
                <small>{fmtPct(salesValue && salesValue > 0 ? breakdownCouponAmount / salesValue : null)}</small>
              </span>
            </div>

            <div className="ipd-cost-row ipd-pl-indent-2 ipd-pl-subtotal">
              <span>広告費 小計</span>
              <span>
                <strong>{fmtYen(adSubtotalAmount)}</strong>
                <small>{fmtPct(salesValue && salesValue > 0 ? adSubtotalAmount / salesValue : null)}</small>
              </span>
            </div>

            <div className="ipd-cost-row ipd-pl-indent-3">
              {renderCostName("RPP広告")}
              <span>
                <strong>{fmtYen(breakdownRppAmount)}</strong>
                <small>{fmtPct(breakdownRppRate)}</small>
              </span>
            </div>

            <div className="ipd-cost-row ipd-pl-indent-3">
              {renderCostName("RPP-EXP")}
              <span>
                <strong>{fmtYen(breakdownRppexpAmount)}</strong>
                <small>{fmtPct(salesValue && salesValue > 0 ? breakdownRppexpAmount / salesValue : null)}</small>
              </span>
            </div>

            <div className="ipd-cost-row ipd-pl-indent-3">
              {renderCostName("その他広告")}
              <span>
                <strong>{fmtYen(breakdownOtherAdsAmount)}</strong>
                <small>{fmtPct(salesValue && salesValue > 0 ? breakdownOtherAdsAmount / salesValue : null)}</small>
              </span>
            </div>
          </div>

          {profitMode === "operating" && (
            <div className="ipd-cost-group">
              <div className="ipd-cost-group-label">固定費</div>
              <div className="ipd-cost-row ipd-pl-indent-1">
                <span>固定費（按分）</span>
                <span>
                  <strong>{fmtYen(fixedCostForPl)}</strong>
                  <small>{fmtPct(salesValue && salesValue > 0 ? fixedCostForPl / salesValue : null)}</small>
                </span>
              </div>
            </div>
          )}

          <div className="ipd-cost-group">
            <div className="ipd-cost-group-label">利益</div>
            <div className={`ipd-cost-row ipd-pl-highlight ${displayProfit !== null && displayProfit < 0 ? "is-loss" : ""}`}>
              <span className="ipd-pl-title"><span className="ipd-step-badge">3</span>{plProfitLabel}（①−②{profitMode === "operating" ? "−固定費" : ""}）</span>
              <span>
                <strong className={displayProfitClass}>{fmtYen(displayProfit)}</strong>
              </span>
            </div>
            <div className={`ipd-cost-row ipd-pl-highlight ${displayRate !== null && displayRate < 0 ? "is-loss" : ""}`}>
              <span className="ipd-pl-title"><span className="ipd-step-badge">4</span>{plProfitLabel}率</span>
              <span>
                <strong className={displayRateClass}>{fmtPct(displayRate)}</strong>
              </span>
            </div>
          </div>

          <div className="ipd-pl-footnote">
            ※ 広告費は、商品単位で紐付け可能な広告は個別に紐付け、紐付けできない広告は独自ロジックで商品へ按分した金額です。
            {/* RN-238：楽天市場広告は商品別に入れていない（お店全体の利益には入っている） */}
            {mall === "rakuten" && (
              <>
                <br />
                ※ 楽天広告（楽天市場・楽天グループの広告）は商品ごとに分けられないため、ここには入れていません（お店全体の利益には入っています）。
              </>
            )}
          </div>
        </div>

        <div className="ipd-card">
          <div className="ipd-card-title">
            1個あたりの利益構造（{profitMode === "margin" ? "限界利益" : "営業利益"}ベース）
            {structureUnitCount > 0 ? ` / 販売個数 ${nf0.format(structureUnitCount)}個で按分` : ""}
          </div>

          {isCostMissing && (
            <div className="ipd-structure-empty">原価未設定のため、一部の利益構造は未算出です</div>
          )}

          <div className="ipd-structure-list">
            {waterfallRows.length === 0 ? (
              <div className="ipd-structure-empty">利益構造データがありません</div>
            ) : (
              waterfallRows.map((row, idx) => {
                const amount = nullableNum(row.amount);
                const barColor = getStructureBarColor(row.label, row.type, row.colorKey);
                return (
                  <div
                    key={`${row.label}-${idx}`}
                    className={`ipd-structure-row ${row.type === "profit" ? "is-profit" : ""}`}
                  >
                    <div className="ipd-structure-label">{row.label}</div>
                    <div className="ipd-structure-bar-area">
                      <div
                        className="ipd-structure-bar"
                        style={{
                          left: `${Math.max(row.leftPct, 0)}%`,
                          width: `${Math.max(row.widthPct, 0)}%`,
                          backgroundColor: barColor,
                          opacity: 0.86,
                        }}
                      />
                    </div>
                    <div
                      className={`ipd-structure-value ${
                        amount === null ? "" : amount < 0 ? "neg" : amount > 0 ? "pos" : ""
                      }`}
                    >
                      {fmtYen(amount)}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>

      {/* 価格シミュレーター */}
      {!isCostMissing && unitSales !== null && (
        <div className="ipd-full-card">
          <div className="ipd-card-title lg">価格シミュレーター</div>
          <div className="ipd-sim-description">
            販売価格・原価・個数を変えたとき、利益がどう変わるかを即座に試算します。
          </div>
          <div className="ipd-sim-row">
            <div className="ipd-sim-field">
              <label className="ipd-sim-label">
                販売価格（現在 {fmtYen(simBasePrice)}
                {isSeparateShipping ? `・送料別 ＋${fmtYen(shippingPerUnit)}/個` : ""}）
              </label>
              <input
                className="ipd-sim-input"
                type="number"
                min={0}
                step={10}
                placeholder={`現在 ${fmtYen(simBasePrice)}`}
                value={simPrice}
                onChange={(e) => setSimPrice(e.target.value)}
              />
            </div>
            <div className="ipd-sim-field">
              <label className="ipd-sim-label">原価（現在 {fmtYen(currentUnitCost)}）</label>
              <input
                className="ipd-sim-input"
                type="number"
                min={0}
                step={1}
                placeholder={`現在 ${fmtYen(currentUnitCost)}`}
                value={simCost}
                onChange={(e) => setSimCost(e.target.value)}
              />
            </div>
            <div className="ipd-sim-field">
              <label className="ipd-sim-label">月間販売個数（現在 {nf0.format(num(summary.units))}個）</label>
              <input
                className="ipd-sim-input"
                type="number"
                min={1}
                step={1}
                placeholder={`現在 ${nf0.format(num(summary.units))}個`}
                value={simUnits}
                onChange={(e) => setSimUnits(e.target.value)}
              />
            </div>
          </div>
          <div className="ipd-sim-hint-box">
            価格を上げると販売個数が減る傾向があります。実際の影響を踏まえて、個数も合わせて調整してください。
          </div>
          {simResult && (
            <div className="ipd-sim-results-grid">
              <div className="ipd-sim-results-col">
                <div className="ipd-sim-col-label">現在</div>
                <div className="ipd-sim-result neutral">
                  <div className="ipd-sim-result-label">1個あたり利益</div>
                  <div className="ipd-sim-result-value pos">{fmtYen(unitProfit)}</div>
                </div>
                <div className="ipd-sim-result neutral">
                  <div className="ipd-sim-result-label">月間利益額</div>
                  <div className="ipd-sim-result-value pos">{fmtYen(simBase.baseProfit)}</div>
                </div>
                <div className="ipd-sim-result neutral">
                  <div className="ipd-sim-result-label">{profitMode === "margin" ? "限界利益率" : "営業利益率"}</div>
                  <div className="ipd-sim-result-value pos">{fmtPct(simBase.baseRate)}</div>
                </div>
              </div>
              <div className="ipd-sim-results-col">
                <div className="ipd-sim-col-label">シミュレーション後</div>
                <div className={`ipd-sim-result ${(simResult.simDisplayUnitProfit ?? 0) >= num(unitProfit) ? "pos" : "neg"}`}>
                  <div className="ipd-sim-result-label">1個あたり利益</div>
                  <div className="ipd-sim-value-row">
                    <div className={`ipd-sim-result-value ${(simResult.simDisplayUnitProfit ?? 0) < 0 ? "neg" : "pos"}`}>{fmtYen(simResult.simDisplayUnitProfit)}</div>
                    <div className="ipd-sim-diff-inline">
                      {(simResult.simDisplayUnitProfit ?? 0) - num(unitProfit) >= 0 ? "+" : ""}
                      {fmtYen((simResult.simDisplayUnitProfit ?? 0) - num(unitProfit))}
                    </div>
                  </div>
                </div>
                <div className={`ipd-sim-result ${(simResult.simDisplayTotalProfit ?? 0) >= num(simBase.baseProfit) ? "pos" : "neg"}`}>
                  <div className="ipd-sim-result-label">月間利益額</div>
                  <div className="ipd-sim-value-row">
                    <div className={`ipd-sim-result-value ${(simResult.simDisplayTotalProfit ?? 0) < 0 ? "neg" : "pos"}`}>{fmtYen(simResult.simDisplayTotalProfit)}</div>
                    <div className="ipd-sim-diff-inline">
                      {(simResult.simDisplayTotalProfit ?? 0) - num(simBase.baseProfit) >= 0 ? "+" : ""}
                      {fmtYen((simResult.simDisplayTotalProfit ?? 0) - num(simBase.baseProfit))}
                    </div>
                  </div>
                </div>
                <div className={`ipd-sim-result ${(simResult.simDisplayRate ?? 0) >= num(simBase.baseRate) ? "pos" : "neg"}`}>
                  <div className="ipd-sim-result-label">{profitMode === "margin" ? "限界利益率" : "営業利益率"}</div>
                  <div className="ipd-sim-value-row">
                    <div className={`ipd-sim-result-value ${(simResult.simDisplayRate ?? 0) < 0 ? "neg" : "pos"}`}>{fmtPct(simResult.simDisplayRate)}</div>
                    <div className="ipd-sim-diff-inline">
                      {((simResult.simDisplayRate ?? 0) - num(simBase.baseRate)) >= 0 ? "+" : ""}
                      {(((simResult.simDisplayRate ?? 0) - num(simBase.baseRate)) * 100).toFixed(1)}pt
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
          {!simResult && simPrice !== "" && (
            <div className="ipd-sim-hint">有効な価格を入力してください</div>
          )}
        </div>
      )}

      <div className="ipd-note-box">
        <span className="ipd-note-icon">💡</span>
        <span>{insightText}</span>
      </div>

      <div className="ipd-full-card">
        <div className="ipd-card-title lg">広告費詳細</div>

        <div className="ipd-table-wrap">
          <table className="ipd-table">
            <thead>
              <tr>
                <th>広告種別</th>
                <th className="right">広告費</th>
                <th className="right">売上比</th>
                <th className="right">ROAS</th>
                <th className="right">720時間ROAS</th>
                <th className="right">12時間ROAS</th>
                <th>紐付け方式</th>
              </tr>
            </thead>
            <tbody>
              {adDetails.length === 0 ? (
                <tr>
                  <td colSpan={7} className="ipd-table-empty">
                    広告費詳細データがありません
                  </td>
                </tr>
              ) : (
                adDetails.map((row, idx) => (
                  <tr key={`${row.adType}-${idx}`}>
                    <td>{row.adType}</td>
                    <td className="right">{fmtYen(row.cost)}</td>
                    <td className="right">{fmtPct(row.costRate)}</td>
                    <td className="right">{fmtPctMaybeWhole(row.roas)}</td>
                    <td className="right">{fmtPctMaybeWhole(row.roas720h)}</td>
                    <td className="right">{fmtPctMaybeWhole(row.roas12h)}</td>
                    <td>{row.linkageType || "—"}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>




      {/* クーポン利益内訳 */}
      {couponDetailDisplayData && (
        <div className="ipd-full-card">
          <div className="ipd-section-header-row">
            <div className="ipd-card-title lg ipd-section-card-title">クーポン利用内訳</div>
            <div className="ipd-section-meta">
              当月：{nf0.format(couponDetailDisplayData.store.totalOrderCount + couponDetailDisplayData.rakuten.totalOrderCount)}件
              <span className="ipd-section-meta-sep">／</span>
              合計値引き
              <strong className="neg"> {fmtYen(-(couponDetailDisplayData.store.totalDiscount + couponDetailDisplayData.rakuten.totalDiscount))}</strong>
            </div>
          </div>
          <div className="ipd-caution-note">
            クーポン明細は対象商品を含む注文をもとに表示しています（注文内売上比で配賦）。
          </div>
          <div className="ipd-coupon-grid">
            {/* 店舗発行 */}
            <div className="ipd-coupon-card store">
              <div className="ipd-coupon-card-header">
                <span className="ipd-coupon-badge store">店舗発行</span>
                <span className="ipd-coupon-total neg">{fmtYen(-couponDetailDisplayData.store.totalDiscount)}</span>
              </div>
              <div className="ipd-coupon-meta">
                {nf0.format(couponDetailDisplayData.store.couponCount)}種類 ／ {nf0.format(couponDetailDisplayData.store.totalOrderCount)}件
              </div>
              {couponDetailDisplayData.store.couponCount > 0 && (
                <button
                  className="ipd-coupon-toggle"
                  type="button"
                  onClick={() => setCouponStoreOpen((v) => !v)}
                >
                  {couponStoreOpen ? "▲ 閉じる" : "▼ 詳細を見る"}
                </button>
              )}
              {couponStoreOpen && (
                <table className="ipd-coupon-table">
                  <thead>
                    <tr>
                      <th>クーポンコード</th>
                      <th>名称</th>
                      <th className="right">利用件数</th>
                      <th className="right">値引き合計</th>
                    </tr>
                  </thead>
                  <tbody>
                    {couponDetailDisplayData.store.details.map((r, i) => (
                      <tr key={i}>
                        <td>{r.couponCode}</td>
                        <td>{r.couponName || "—"}</td>
                        <td className="right">{nf0.format(r.orderCount)}件</td>
                        <td className="right neg">{fmtYen(-r.totalDiscount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* 楽天発行 */}
            <div className="ipd-coupon-card rakuten">
              <div className="ipd-coupon-card-header">
                <span className="ipd-coupon-badge rakuten">楽天発行</span>
                <span className="ipd-coupon-total neg">{fmtYen(-couponDetailDisplayData.rakuten.totalDiscount)}</span>
              </div>
              <div className="ipd-coupon-meta">
                {nf0.format(couponDetailDisplayData.rakuten.couponCount)}種類 ／ {nf0.format(couponDetailDisplayData.rakuten.totalOrderCount)}件
              </div>
              {couponDetailDisplayData.rakuten.couponCount > 0 && (
                <button
                  className="ipd-coupon-toggle"
                  type="button"
                  onClick={() => setCouponRakutenOpen((v) => !v)}
                >
                  {couponRakutenOpen ? "▲ 閉じる" : "▼ 詳細を見る"}
                </button>
              )}
              {couponRakutenOpen && (
                <table className="ipd-coupon-table">
                  <thead>
                    <tr>
                      <th>クーポンコード</th>
                      <th>名称</th>
                      <th className="right">利用件数</th>
                      <th className="right">値引き合計</th>
                    </tr>
                  </thead>
                  <tbody>
                    {couponDetailDisplayData.rakuten.details.map((r, i) => (
                      <tr key={i}>
                        <td>{r.couponCode}</td>
                        <td>{r.couponName || "—"}</td>
                        <td className="right">{nf0.format(r.orderCount)}件</td>
                        <td className="right neg">{fmtYen(-r.totalDiscount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
          {couponSummaryCards.length > 0 ? (
            <div className="ipd-coupon-summary-grid">
              {couponSummaryCards.map((card) => (
                <div key={card.label} className={`ipd-coupon-summary-card ${card.tone}`}>
                  <div className="ipd-coupon-summary-label">{card.label}</div>
                  <div className={`ipd-coupon-summary-value ${card.tone}`}>{card.value}</div>
                  <div className="ipd-coupon-summary-sub">{card.sub}</div>
                </div>
              ))}
            </div>
          ) : null}

          {couponReferenceSummaryCards.length > 0 ? (
            <>
              <div className="ipd-caution-note" style={{ marginTop: 12 }}>
                参考値は、対象商品の入った注文のクーポン額を注文内売上比で配賦したものです。現行の店舗全体按分とは別に確認できます。
              </div>
              <div className="ipd-coupon-summary-grid" style={{ marginTop: 12 }}>
                {couponReferenceSummaryCards.map((card) => (
                  <div key={card.label} className={`ipd-coupon-summary-card ${card.tone}`}>
                    <div className="ipd-coupon-summary-label">{card.label}</div>
                    <div className={`ipd-coupon-summary-value ${card.tone}`}>{card.value}</div>
                    <div className="ipd-coupon-summary-sub">{card.sub}</div>
                  </div>
                ))}
              </div>
            </>
          ) : null}
        </div>
      )}

      {/* 利益推移グラフ */}
      <div className="ipd-full-card">
        <div className="ipd-trend-header-row">
          <div className="ipd-card-title lg ipd-trend-card-title">利益推移</div>
          <div className="ipd-trend-period-tabs" role="tablist" aria-label="利益推移期間">
            <button
              className={`ipd-trend-tab ${trendPeriod === "month" ? "active" : ""}`}
              type="button"
              onClick={() => setTrendPeriod("month")}
            >
              当月全体
            </button>
            <button
              className={`ipd-trend-tab ${trendPeriod === "2w" ? "active" : ""}`}
              type="button"
              onClick={() => setTrendPeriod("2w")}
            >
              直近2週間
            </button>
            <button
              className={`ipd-trend-tab ${trendPeriod === "30d" ? "active" : ""}`}
              type="button"
              onClick={() => setTrendPeriod("30d")}
            >
              直近30日
            </button>
          </div>
        </div>
        {isSeparateShipping ? (
          <div className="ipd-trend-note">※ 利益推移の売上は、お客様が払った送料を含みません</div>
        ) : null}
        {trendChartData.length === 0 ? (
          <div className="ipd-trend-empty">
            {trendFetchFailed
              ? trendFetchMessage || "日次推移データの取得に失敗しました"
              : "日次推移データがありません"}
          </div>
        ) : (
          <>
            <div className="ipd-trend-controls">
              <div className="ipd-trend-control-group">
                <div className="ipd-trend-section-label">目的別の利益効果検証</div>
                <div className="ipd-trend-button-row">
                  {trendPresets.map((preset, index) => (
                    <button
                      key={preset.label}
                      className={`ipd-trend-pill ${activeTrendPreset === index ? "active" : ""}`}
                      type="button"
                      onClick={() => handleTrendPreset(index)}
                    >
                      {preset.label}
                    </button>
                  ))}
                </div>
                {currentTrendPurpose ? (
                  <div className="ipd-trend-purpose">{currentTrendPurpose}</div>
                ) : null}
              </div>

              <div className="ipd-trend-control-group">
                <div className="ipd-trend-section-label">単一指標</div>
                <div className="ipd-trend-button-row">
                  {(["profitAmount", "sales", "profitRate", "units"] as TrendMetricKey[]).map((metric) => (
                    <button
                      key={metric}
                      className={`ipd-trend-pill ${
                        activeTrendPreset === null && trendPrimaryMetric === metric ? "active" : ""
                      }`}
                      type="button"
                      onClick={() => handleTrendSingleMetric(metric)}
                    >
                      {trendMetricLabels[metric]}
                    </button>
                  ))}
                </div>
                <div className="ipd-trend-caption">
                  現在のAPIでは、売上・利益額・利益率・個数を日次で切り替え表示できます。
                </div>
              </div>
            </div>

            <div className="ipd-trend-chart">
              <ResponsiveContainer width="100%" height={228}>
                <ComposedChart data={visibleTrendData} margin={{ top: 10, right: trendSecondaryMetric && !useSharedScaleMode ? 18 : 6, left: 6, bottom: 10 }}>
                {trendWeekendRanges.map((range, index) => (
                  <ReferenceArea
                    key={`weekend-${range.x1}-${range.x2}-${index}`}
                    x1={range.x1}
                    x2={range.x2}
                    yAxisId="left"
                    fill="rgba(180,200,240,0.13)"
                    strokeOpacity={0}
                  />
                ))}
                <CartesianGrid strokeDasharray="3 3" stroke="#ebebea" vertical={false} />
                {visibleTrendData
                  .filter((row) => row.isSpecialDay)
                  .map((row) => (
                    <ReferenceLine
                      key={`special-${row.date}`}
                      x={row.date}
                      stroke="#378ADD55"
                      strokeDasharray="3 3"
                    />
                  ))}
                <XAxis
                  dataKey="date"
                  tickFormatter={(v: string) => dayjs(v).format("M/D")}
                  tick={{ fontSize: 10, fill: "#bbb" }}
                  minTickGap={10}
                  tickLine={false}
                  axisLine={false}
                />
                <YAxis
                  yAxisId="left"
                  orientation="left"
                  tickFormatter={(v: number) => fmtTrendAxis(trendPrimaryMetric, v)}
                  tick={{ fontSize: 10, fill: "#bbb" }}
                  tickLine={false}
                  axisLine={false}
                  width={34}
                />
                {trendSecondaryMetric && !useSharedScaleMode ? (
                  <YAxis
                    yAxisId="right"
                    orientation="right"
                    tickFormatter={(v: number) => fmtTrendAxis(trendSecondaryMetric, v)}
                    tick={{ fontSize: 10, fill: TREND_METRIC_STYLES[trendSecondaryMetric].color }}
                    tickLine={false}
                    axisLine={false}
                    width={34}
                  />
                ) : null}
                <Tooltip
                  formatter={(value: number | undefined, name: string | undefined) => {
                    const metric = (Object.entries(trendMetricLabels).find(([, label]) => label === name)?.[0] ?? "sales") as TrendMetricKey;
                    return [fmtTrendValue(metric, value), name ?? ""];
                  }}
                  labelFormatter={(label: string) => dayjs(label).format("YYYY/M/D")}
                  contentStyle={{ fontSize: 12, borderRadius: 6, border: "1px solid #e8e8e5" }}
                />
                <Area
                  yAxisId="left"
                  dataKey={trendPrimaryMetric}
                  type="linear"
                  stroke="none"
                  fill={TREND_METRIC_STYLES[trendPrimaryMetric].color}
                  fillOpacity={0.08}
                  isAnimationActive={false}
                />
                <Line
                  yAxisId="left"
                  dataKey={trendPrimaryMetric}
                  name={trendMetricLabels[trendPrimaryMetric]}
                  type="linear"
                  connectNulls
                  stroke={TREND_METRIC_STYLES[trendPrimaryMetric].color}
                  strokeWidth={2.6}
                  strokeOpacity={0.95}
                  strokeDasharray={TREND_METRIC_STYLES[trendPrimaryMetric].dashed ? "5 3" : undefined}
                  dot={({ cx, cy, payload }: any) => (
                    <circle
                      cx={cx}
                      cy={cy}
                      r={payload?.isSpecialDay ? 4.4 : 2.8}
                      fill={TREND_METRIC_STYLES[trendPrimaryMetric].color}
                      stroke="#fff"
                      strokeWidth={1.4}
                    />
                  )}
                  activeDot={{ r: 4 }}
                />
                {trendSecondaryMetric ? (
                  <>
                    <Area
                      yAxisId={useSharedScaleMode ? "left" : "right"}
                      dataKey={trendSecondaryMetric}
                      type="linear"
                      stroke="none"
                      fill={TREND_METRIC_STYLES[trendSecondaryMetric].color}
                      fillOpacity={0.06}
                      isAnimationActive={false}
                    />
                    <Line
                      yAxisId={useSharedScaleMode ? "left" : "right"}
                      dataKey={trendSecondaryMetric}
                      name={trendMetricLabels[trendSecondaryMetric]}
                      type="linear"
                      connectNulls
                      stroke={TREND_METRIC_STYLES[trendSecondaryMetric].color}
                      strokeWidth={2.2}
                      strokeOpacity={0.9}
                      strokeDasharray={TREND_METRIC_STYLES[trendSecondaryMetric].dashed ? "5 3" : undefined}
                      dot={({ cx, cy, payload }: any) => (
                        <circle
                          cx={cx}
                          cy={cy}
                          r={payload?.isSpecialDay ? 4.1 : 2.5}
                          fill={TREND_METRIC_STYLES[trendSecondaryMetric].color}
                          stroke="#fff"
                          strokeWidth={1.2}
                        />
                      )}
                      activeDot={{ r: 4 }}
                    />
                  </>
                ) : null}
                </ComposedChart>
              </ResponsiveContainer>
            </div>

            <div className="ipd-trend-legend-row">
              <div className="ipd-trend-legend-item">
                <span
                  className="ipd-trend-legend-dot"
                  style={{ background: TREND_METRIC_STYLES[trendPrimaryMetric].color }}
                />
                <span>{trendMetricLabels[trendPrimaryMetric]}{trendSecondaryMetric && !useSharedScaleMode ? "（左軸）" : ""}</span>
              </div>
              {trendSecondaryMetric ? (
                <div className="ipd-trend-legend-item">
                  <span
                    className="ipd-trend-legend-line"
                    style={{ borderTopColor: TREND_METRIC_STYLES[trendSecondaryMetric].color }}
                  />
                  <span>{trendMetricLabels[trendSecondaryMetric]}{useSharedScaleMode ? "（同一軸）" : "（右軸）"}</span>
                </div>
              ) : null}
              <div className="ipd-trend-legend-item ipd-trend-legend-note">★ 0・5のつく日</div>
            </div>

            <div className="ipd-trend-summary-grid">
              {trendSummaryItems.map((item, index) => (
                <div className="ipd-trend-stat" key={`${item.label}-${index}`}>
                  <div className="ipd-trend-stat-label">{item.label}</div>
                  <div className={`ipd-trend-stat-value ${item.className}`}>{item.value}</div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>


      </div>
    </div>
  );
}